import Swiper from 'swiper';
import { Navigation, Scrollbar, Pagination, Autoplay, FreeMode } from 'swiper/modules';
import { debounce, throttle } from 'lodash';
import Cookies from 'js-cookie';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import PhotoSwipe from 'photoswipe';

const execOnLoad = (initFunction, selector) => {
  if (!initFunction || !selector) return;

  if (document.readyState != 'loading') {
    [].forEach.call(document.querySelectorAll(selector), (el) => {
      initFunction(el);
    });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      [].forEach.call(document.querySelectorAll(selector), (el) => {
        initFunction(el);
      });
    });
  }
};

/**
 * Formats a price value according to a specified format template.
 *
 * The function converts the given value into various formatted string representations
 * based on placeholders in the `format` string. It supports multiple formats including
 * comma separators, dot separators, and space separators.
 *
 * @param {Object} params - Parameters object.
 * @param {string} [params.format=window?.dAgency?.strings?.moneyFormat] - A string template for the format.
 *        This template can contain placeholders like `{{amount}}`, `{{amount_no_decimals}}`, etc.
 * @param {number} params.value - The price value to format, provided as an integer in cents.
 *
 * @returns {string|undefined} The formatted price string based on the format template. If `value` or `format` is missing, returns `undefined`.
 */
const formatPrice = ({
  format = window?.dAgency?.strings?.moneyFormat,
  value,
}) => {
  if ((!value && value != 0) || !format) return;
  const formattedValue = (parseInt(value) / 100).toFixed(2);
  const placeholders = [
    {
      key: 'amount',
      formatter: () => {
        const splittedValue = formattedValue.split('.');
        const integerPart = splittedValue[0].replace(
          /\B(?=(\d{3})+(?!\d))/g,
          ',',
        );
        const decimalPart = splittedValue[1] ?? '00';
        return `${integerPart}.${decimalPart}`;
      },
    },
    {
      key: 'amount_no_decimals',
      formatter: () => Math.round(formattedValue),
    },
    {
      key: 'amount_with_comma_separator',
      formatter: () => {
        const splittedValue = formattedValue.split('.');
        const integerPart = splittedValue[0].replace(
          /\B(?=(\d{3})+(?!\d))/g,
          '.',
        );
        const decimalPart = splittedValue[1] ?? '00';
        return `${integerPart},${decimalPart}`;
      },
    },
    {
      key: 'amount_no_decimals_with_comma_separator',
      formatter: () =>
        `${Math.round(formattedValue)}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
    },
    {
      key: 'amount_with_apostrophe_separator',
      formatter: () => {
        const splittedValue = formattedValue.split('.');
        const integerPart = splittedValue[0].replace(
          /\B(?=(\d{3})+(?!\d))/g,
          "'",
        );
        const decimalPart = splittedValue[1] ?? '00';
        return `${integerPart}.${decimalPart}`;
      },
    },
    {
      key: 'amount_no_decimals_with_space_separator',
      formatter: () =>
        `${Math.round(formattedValue)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' '),
    },
    {
      key: 'amount_with_space_separator',
      formatter: () => {
        const splittedValue = formattedValue.split('.');
        const integerPart = splittedValue[0].replace(
          /\B(?=(\d{3})+(?!\d))/g,
          ' ',
        );
        const decimalPart = splittedValue[1] ?? '00';
        return `${integerPart},${decimalPart}`;
      },
    },
    {
      key: 'amount_with_period_and_space_separator',
      formatter: () => {
        const splittedValue = formattedValue.split('.');
        const integerPart = splittedValue[0].replace(
          /\B(?=(\d{3})+(?!\d))/g,
          ' ',
        );
        const decimalPart = splittedValue[1] ?? '00';
        return `${integerPart}.${decimalPart}`;
      },
    },
  ];

  return placeholders.reduce(
    (acc, placeholder) =>
      acc.replaceAll(
        new RegExp(`{{[ ]*${placeholder.key}[ ]*}}`, 'g'),
        placeholder.formatter(value),
      ),
    format,
  );
};

/**
 * Initializes a number input field with increment and decrement buttons.
 *
 * @param {Object} params - The parameters for initializing the number field.
 * @param {HTMLElement} params.plusBtn - The button element used to increase the value.
 * @param {HTMLElement} params.minusBtn - The button element used to decrease the value.
 * @param {HTMLElement} params.field - The field element where the value is displayed or entered.
 *
 * @throws {Error} Will log an error if `plusBtn`, `minusBtn`, or `field` is missing.
 */
const initNumberField = ({ plusBtn, minusBtn, field }) => {
  if (!plusBtn || !minusBtn || !field) {
    console.error('Cannot init number field, missing arguments.');
    return;
  }

  const min = field.dataset.min ? parseInt(field.dataset.min, 10) : null;
  const max = field.dataset.max ? parseInt(field.dataset.max, 10) : null;

  const updateButtonStates = () => {
    if (min !== null) {
      if (value <= min) {
        minusBtn.tagName === 'BUTTON'
          ? (minusBtn.disabled = true)
          : minusBtn.setAttribute('disabled', '');
      } else {
        minusBtn.tagName === 'BUTTON'
          ? (minusBtn.disabled = false)
          : minusBtn.removeAttribute('disabled');
      }
    }
    if (max !== null) {
      if (value >= max) {
        plusBtn.tagName === 'BUTTON'
          ? (plusBtn.disabled = true)
          : plusBtn.setAttribute('disabled', '');
      } else {
        plusBtn.tagName === 'BUTTON'
          ? (plusBtn.disabled = false)
          : plusBtn.removeAttribute('disabled');
      }
    }
  };

  const updateValue = (newValue) => {
    const clampedValue = Math.max(
      min ?? -Infinity,
      Math.min(max ?? Infinity, newValue),
    );
    field.tagName === 'INPUT'
      ? (field.value = clampedValue)
      : (field.innerText = clampedValue);
    if (field.tagName === 'INPUT' && value != clampedValue)
      ['input', 'change'].forEach((evt) =>
        field.dispatchEvent(new Event(evt, { bubbles: true })),
      );
    value = clampedValue;
    updateButtonStates();
  };

  let value = parseInt(
    field.tagName === 'INPUT' ? field.value : field.innerText,
  );
  updateValue(value);

  plusBtn.addEventListener('click', (e) => {
    e.preventDefault();
    updateValue(value + 1);
  });

  minusBtn.addEventListener('click', (e) => {
    e.preventDefault();
    updateValue(value - 1);
  });
};

var sm = "360px";
var md = "768px";
var lg = "1024px";
var xl = "1440px";
var breakpoints = {
	sm: sm,
	md: md,
	lg: lg,
	xl: xl
};

if (!window.dAgency) window.dAgency = {};
window.dAgency.initCarousel = (context) => {
  const snippetContainer = context.classList.contains('.carousel__container')
    ? context
    : context.closest('.carousel__container');
  if (!snippetContainer) return;

  const sliderWrapper = snippetContainer.querySelector(
    '.carousel__slider-wrapper',
  );
  const slides = snippetContainer.querySelectorAll('.swiper-slide');
  if (!sliderWrapper || slides.length <= 1) return;

  const leftArrow = snippetContainer.querySelector('.carousel__arrow--left');
  const rightArrow = snippetContainer.querySelector('.carousel__arrow--right');
  const scrollbar = snippetContainer.querySelector('.carousel__scrollbar');
  const options = {
    modules: [
      ...(leftArrow && rightArrow ? [Navigation] : []),
      ...(scrollbar ? [Scrollbar] : []),
    ],
    ...(scrollbar ? { scrollbar: { el: scrollbar, draggable: true } } : {}),
    ...(leftArrow && rightArrow
      ? { navigation: { prevEl: leftArrow, nextEl: rightArrow } }
      : {}),
    spaceBetween: sliderWrapper.dataset.mobileGap ?? 0,
    slidesPerView: sliderWrapper.dataset.mobileSlides ?? 1,
    breakpoints: {
      [parseInt(breakpoints.lg.replace('px', ''))]: {
        spaceBetween: sliderWrapper.dataset.desktopGap ?? 0,
        slidesPerView: sliderWrapper.dataset.desktopSlides ?? 1,
      },
    },
  };
  new Swiper(sliderWrapper, options);
};

execOnLoad(window.dAgency.initCarousel, '.carousel__container');

/**
 * Event type for before cart change.
 * @constant {string}
 */
const beforeCartChangeType = 'beforeCartChange';

/**
 * Event type for after cart change.
 * @constant {string}
 */
const afterCartChangeType = 'afterCartChange';

/**
 * Retrieves the current cart information.
 *
 * @param {Object} [callbacks] - Optional callback functions.
 * @param {Function} [callbacks.beforeFetch] - Function to call before fetching the cart data.
 * @param {Function} [callbacks.afterFetch] - Function to call after successfully fetching the cart data.
 * @param {Function} [callbacks.errorCallback] - Function to call if an error occurs during the fetch.
 *
 * @returns {Promise<Object>} A promise that resolves to the cart information object.
 * @throws {Error} Will throw an error if the network request fails or if the response is not ok.
 */
const getCart = async ({
  beforeFetch,
  afterFetch,
  errorCallback,
} = {}) => {
  if (typeof beforeFetch !== 'undefined') await beforeFetch();
  const cart = await fetch(`${window.Shopify.routes.root}cart.js`)
    .then((res) => {
      if (!res.ok)
        throw new Error(`Fetch result was not ok. Requested URL: ${res.url}`);
      return res.json();
    })
    .catch(async (err) => {
      if (typeof errorCallback !== 'undefined') await errorCallback(err);
      console.error('Fetch cart info failed:', err);
      throw err;
    });
  if (typeof afterFetch !== 'undefined') await afterFetch(cart);
  return cart;
};

/**
 * Adds an item to the cart.
 *
 * @param {Object} [options] - Options for adding the item to the cart.
 * @param {HTMLFormElement} [options.form] - HTML form to send with the request.
 * @param {number} [options.id] - ID of the item to add.
 * @param {number} [options.quantity=1] - Quantity of the item to add. Defaults to `1`.
 * @param {Object} [callbacks] - Optional callback functions for different stages of the request.
 * @param {Function} [callbacks.beforeFetch] - Function to call before sending the request.
 * @param {Function} [callbacks.afterFetch] - Function to call after successfully adding the item to the cart.
 * @param {Function} [callbacks.errorCallback] - Function to call if an error occurs during the addition.
 *
 * @returns {Promise<Object>} A promise that resolves to the updated cart object.
 * @throws {Error} Will throw an error if the network request fails or if the response is not ok.
 */
const addItemToCart = async ({
  form,
  id,
  quantity = 1,
  callbacks: { beforeFetch, afterFetch, errorCallback } = {},
} = {}) => {
  if (!form && !id) {
    console.error('At least one between form or id needs to be used');
    return false;
  }
  const formData = form ? new FormData(form) : null;
  if (form && !formData?.get('id')) {
    console.error('Missing ID field from form');
    return false;
  }
  const events = {
    [`${beforeCartChangeType}`]: new CustomEvent(beforeCartChangeType),
    [`${afterCartChangeType}`]: new CustomEvent(afterCartChangeType),
  };
  document.dispatchEvent(events[beforeCartChangeType]);
  if (typeof beforeFetch !== 'undefined') await beforeFetch();

  const cart = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: [
        form
          ? {
              id: formData.get('id'),
              quantity: parseInt(formData.get('quantity')) ?? 1,
            }
          : { id, quantity },
      ],
    }),
  })
    .then(async (res) => {
      if (!res.ok) {
        const error = new Error(
          `Fetch result was not ok. Requested URL: ${res.url}`,
        );
        error.data = await res.json();
        throw error;
      }
      return res.json();
    })
    .catch(async (err) => {
      if (typeof errorCallback !== 'undefined') await errorCallback(err);
      console.error('Add to cart failed:', err);
      throw err;
    });
  events[afterCartChangeType].cart = cart;
  document.dispatchEvent(events[afterCartChangeType]);
  if (typeof afterFetch !== 'undefined') await afterFetch(cart);
  return cart;
};

/**
 * Changes the quantity or properties of an item in the cart.
 *
 * @param {Object} [options] - Options for changing the cart item.
 * @param {number} [options.line] - Line number of the item in the cart.
 * @param {number} [options.variantId] - ID of the product variant to change.
 * @param {string} [options.key] - Key of the item in the cart.
 * @param {number} [options.quantity] - New quantity for the item.
 * @param {Object} [options.properties] - New properties to set for the item.
 * @param {boolean} [options.keepOldProperties=true] - Whether to keep existing properties when updating. Defaults to `true`.
 * @param {Object} [callbacks] - Optional callback functions for different stages of the request.
 * @param {Function} [callbacks.beforeFetch] - Function to call before sending the request.
 * @param {Function} [callbacks.afterFetch] - Function to call after successfully changing the cart item.
 * @param {Function} [callbacks.errorCallback] - Function to call if an error occurs during the change.
 *
 * @returns {Promise<Object>} A promise that resolves to the updated cart object.
 * @throws {Error} Will throw an error if the network request fails or if the response is not ok.
 */
const changeCart = async ({
  line,
  variantId,
  key,
  quantity,
  properties,
  keepOldProperties = true,
  callbacks: { beforeFetch, afterFetch, errorCallback } = {},
} = {}) => {
  if (!line && !variantId && !key) {
    console.error(
      'At least one between line, key or variantId needs to be used',
    );
    return false;
  }
  const oldProperties = {};
  if (!!properties && keepOldProperties) {
    const { items } = await getCart();
    const { properties } = items.find(
      ({ key: itemKey, variant_id }, index) =>
        index + 1 == line || itemKey == key || variantId == variant_id,
    );
    Object.keys(properties).forEach(
      (property) => (oldProperties[property] = properties[property]),
    );
  }

  const options = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...(line ? { line } : { id: key ?? variantId }),
      ...(properties && {
        properties: Object.entries({
          ...oldProperties,
          ...properties,
        }).reduce(
          (newProperties, property) =>
            property[1] !== ''
              ? {
                  ...newProperties,
                  [property[0]]: property[1],
                }
              : newProperties,
          {},
        ),
      }),
      quantity,
    }),
  };
  const events = {
    [`${beforeCartChangeType}`]: new CustomEvent(beforeCartChangeType),
    [`${afterCartChangeType}`]: new CustomEvent(afterCartChangeType),
  };
  document.dispatchEvent(events[beforeCartChangeType]);
  if (typeof beforeFetch !== 'undefined') await beforeFetch();
  const cart = await fetch(
    `${window.Shopify.routes.root}cart/change.js`,
    options,
  )
    .then((res) => {
      if (!res.ok)
        throw new Error(`Fetch result was not ok. Requested URL: ${res.url}`);
      return res.json();
    })
    .catch(async (err) => {
      if (typeof errorCallback !== 'undefined') await errorCallback(err);
      console.error('Cart change failed:', err);
      throw err;
    });
  events[afterCartChangeType].cart = cart;
  document.dispatchEvent(events[afterCartChangeType]);
  if (typeof afterFetch !== 'undefined') await afterFetch(cart);
  return cart;
};

/**
 * Fetches product information from a given URL or using a product handle.
 *
 * This function makes an HTTP GET request to retrieve product details in JSON format. If neither `url` nor `handle` is provided, it returns `false`.
 *
 * @param {Object} params - The parameters for fetching the product.
 * @param {string} [params.url] - The URL to fetch the product information from. If not provided, the `handle` will be used.
 * @param {string} [params.handle] - The product handle. Used to construct the URL if `url` is not provided.
 * @returns {Promise<Object|boolean>} A promise that resolves to the product information object if successful, or `false` if neither `url` nor `handle` is provided.
 * @throws {Error} Will throw an error if the network request fails or if the response is not ok.
 */
const getProduct = async ({ url, handle }) => {
  if (!handle && !url) return false;
  return await fetch(
    `${url ? url : `${window.Shopify.routes.root}products/${handle}`}.js`,
  )
    .then((res) => {
      if (!res.ok)
        throw new Error(`Fetch result was not ok. Requested URL: ${res.url}`);
      return res.json();
    })
    .catch((err) => {
      console.error('Fetch product info failed:', err);
      throw err;
    });
};

/**
 * Event type for before variant change.
 * @constant {string}
 */
const beforeVariantChangeType = 'beforeVariantChange';

/**
 * Event type for after variant change.
 * @constant {string}
 */
const afterVariantChangeType = 'afterVariantChange';

/**
 * Retrieves a product variant based on the selected options in the form.
 *
 * @param {Object} params - Parameters object.
 * @param {HTMLFormElement} params.form - The form element containing product options.
 * @param {string} params.productOptions - CSS selector for the product option inputs.
 * @param {number} params.expectedOptionsLength - Expected number of product options.
 * @param {Array<Object>} params.variants - List of product variants.
 *
 * @returns {Object|undefined} The selected variant object or undefined if not found.
 */
const getVariantFromOptions = ({
  form,
  productOptions,
  expectedOptionsLength,
  variants,
}) => {
  if (!form || !variants || !productOptions) return;
  const options = [...form.querySelectorAll(productOptions)].map((input) => ({
    name: input.name,
    value: input.value,
  }));
  if (options.length !== expectedOptionsLength) {
    console.error(
      `Unexpected number of product options. Expected value: ${expectedOptionsLength} Provided value: ${options.length}`,
    );
    return;
  }
  const variant = variants.find((variant) =>
    options.every((option) => variant[option.name] === option.value),
  );
  if (!variant) {
    console.error(
      `Cannot find variant with the selected options: ${JSON.stringify(options)}`,
    );
    return;
  }
  return variant;
};

/**
 * Updates the product price display in the specified wrapper element.
 *
 * @param {Object} params - Parameters object.
 * @param {HTMLElement} params.wrapper - The element containing price elements to update.
 * @param {string} [params.compareAtPriceSelector='[data-compare-at-price]'] - CSS selector for the compare-at price elements.
 * @param {string} [params.priceSelector='[data-price]'] - CSS selector for the price elements.
 * @param {number} [params.compareAtPrice] - The compare-at price to display.
 * @param {number} [params.price] - The current price to display.
 * @param {Object} [params.variant] - The product variant object containing price information.
 */
const updateProductPrice = ({
  wrapper,
  compareAtPriceSelector = '[data-compare-at-price]',
  priceSelector = '[data-price]',
  compareAtPrice,
  price,
  variant,
}) => {
  if (!wrapper) return;
  const compareAtPriceContent = formatPrice({
    value: compareAtPrice ?? variant?.compare_at_price,
  });
  wrapper
    .querySelectorAll(compareAtPriceSelector)
    .forEach((compareAtPriceWrapper) => {
      compareAtPriceWrapper.innerText = compareAtPriceContent ?? '';
      compareAtPriceWrapper.style.display = compareAtPriceContent ? '' : 'none';
    });
  const priceContent = formatPrice({
    value: price ?? variant?.price,
  });
  wrapper.querySelectorAll(priceSelector).forEach((priceWrapper) => {
    priceWrapper.innerText = priceContent ?? '';
    priceWrapper.style.display = priceContent ? '' : 'none';
  });
};

/**
 * Initializes the product form, sets up event listeners, and handles product variant changes.
 *
 * @param {Object} params - Parameters object.
 * @param {HTMLFormElement} params.form - The form element to initialize.
 * @param {Object} [params.selectors] - Object containing CSS selectors for form elements.
 * @param {string} [params.selectors.id='[name="id"]'] - CSS selector for the ID field.
 * @param {string} [params.selectors.submit='[type="submit"]'] - CSS selector for the submit element.
 * @param {string} [params.selectors.productOptions='input[name*="option"]:checked'] - CSS selector for the product options.
 * @param {string} [params.selectors.compareAtPrice='[data-compare-at-price]'] - CSS selector for the compare-at price elements.
 * @param {string} [params.selectors.price='[data-price]'] - CSS selector for the price elements.
 * @param {Object} [params.product] - Object containing product information and selectors.
 * @param {string} [params.product.jsonSelector='[data-product-json]'] - CSS selector for the product JSON script tag.
 * @param {string} [params.product.url] - URL to fetch product data if not available in the JSON.
 * @param {string} [params.product.handle] - Handle to fetch product data if not available in the JSON.
 * @param {Object} [params.callbacks] - Object containing callback functions for form events.
 * @param {Function} [params.callbacks.beforeVariantChange] - Function to call before variant change.
 * @param {Function} [params.callbacks.afterVariantChange] - Function to call after variant change.
 * @param {Function} [params.callbacks.beforeSubmit] - Function to call before form submit.
 * @param {Function} [params.callbacks.afterSubmit] - Function to call after form submit.
 * @param {Function} [params.callbacks.errorCallback] - Function to call if the form submits enounters an error.
 */
const initProductForm = async ({
  form,
  selectors: {
    id = '[name="id"]',
    submit = '[type="submit"]',
    productOptions = 'input[name*="option"]:checked',
    compareAtPrice = '[data-compare-at-price]',
    price = '[data-price]',
  } = {
    id: '[name="id"]',
    productOptions: 'input[name*="option"]:checked',
    compareAtPrice: '[data-compare-at-price]',
    price: '[data-price]',
  },
  product: { jsonSelector = '[data-product-json]', url, handle } = {
    jsonSelector: '[data-product-json]',
  },
  callbacks: {
    beforeVariantChange,
    afterVariantChange,
    beforeSubmit,
    afterSubmit,
    errorCallback,
  } = {},
}) => {
  if (!form) {
    console.error('Unable to initialize form, missing form element.');
    return;
  }
  const idField = form.querySelector(id);
  const submitEl = form.querySelector(submit);
  const jsonScript = form.querySelector(jsonSelector);
  const productJson = jsonScript
    ? JSON.parse(jsonScript.innerText)
    : await getProduct({ url, handle });
  if (!productJson) {
    console.error(
      `Cannot find product Object. script selector: ${jsonSelector}, product selectors: ${JSON.stringify({ url, handle })}`,
    );
    return;
  }
  if (!idField) {
    console.error(`Missing ID field, selector used: ${id}`);
    return;
  }

  var selectedVariant = getVariantFromOptions({
    form,
    productOptions,
    expectedOptionsLength: productJson.options.length,
    variants: productJson.variants,
  });
  if (submitEl)
    submitEl.disabled =
      typeof selectedVariant.available !== 'undefined' &&
      !selectedVariant.available;

  const events = {
    [`${beforeVariantChangeType}`]: new CustomEvent(beforeVariantChangeType),
    [`${afterVariantChangeType}`]: new CustomEvent(afterVariantChangeType),
  };
  Object.keys(events).forEach((eventType) => {
    events[eventType].variant = selectedVariant;
    events[eventType].product = productJson;
  });

  form.addEventListener('change', async () => {
    form.dispatchEvent(events[beforeVariantChangeType]);
    if (typeof beforeVariantChange === 'function')
      await beforeVariantChange({
        variant: selectedVariant,
        product: productJson,
      });
    selectedVariant = getVariantFromOptions({
      form,
      productOptions,
      expectedOptionsLength: productJson.options.length,
      variants: productJson.variants,
    });
    updateProductPrice({
      wrapper: form,
      compareAtPriceSelector: compareAtPrice,
      priceSelector: price,
      variant: selectedVariant,
    });
    Object.keys(events).forEach(
      (eventType) => (events[eventType].variant = selectedVariant),
    );
    idField.value = selectedVariant.id;
    if (submitEl)
      submitEl.disabled =
        typeof selectedVariant.available !== 'undefined' &&
        !selectedVariant.available;
    form.dispatchEvent(events[afterVariantChangeType]);
    if (typeof afterVariantChange === 'function')
      await afterVariantChange({
        variant: selectedVariant,
        product: productJson,
      });
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    await addItemToCart({
      form,
      callbacks: {
        beforeFetch: beforeSubmit,
        afterFetch: afterSubmit,
        errorCallback,
      },
    });
  });
};

const openModal = (modal) => {
  if (!modal) return;
  const foundModal =
    typeof modal === 'string' ? document.querySelector(modal) : modal;
  if (!foundModal) return;

  foundModal.style.display = 'block';
  document.querySelector('html').style.overflow = 'hidden';
  setTimeout(() => {
    window.requestAnimationFrame(() => {
      foundModal.classList.add('open');
      foundModal.style.display = '';
    });
  }, 100);
};

const closeModal = (modal) => {
  if (!modal) return;
  const foundModal =
    typeof modal === 'string' ? document.querySelector(modal) : modal;
  if (!foundModal) return;

  const transitionDuration =
    parseFloat(
      getComputedStyle(foundModal).transitionDuration.replace('s', ''),
    ) * 1000;
  foundModal.style.display = 'block';
  window.requestAnimationFrame(() => {
    foundModal.classList.remove('open');
    setTimeout(() => {
      foundModal.style.display = '';
      document.querySelector('html').style.overflow = '';
    }, transitionDuration);
  });
};

const initModals = (root = document) => {
  if (!root) return;
  const rootEl = typeof root === 'string' ? document.querySelector(root) : root;
  if (!rootEl) return;

  document.querySelectorAll('.modal').forEach((modal) => {
    modal
      .querySelectorAll('.modal__backdrop , .modal__close')
      .forEach((closer) =>
        closer.addEventListener('click', () => closeModal(modal)),
      );
    if (modal.id)
      rootEl.querySelectorAll(`a[href*="${modal.id}"]`).forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          openModal(modal);
        }),
      );
  });
};

/**
 * Finds the minicart element within the specified root element.
 *
 * @param {Document|HTMLElement} [root=document] - The root element to search within.
 * @returns {HTMLElement|null} The minicart element if found, otherwise null.
 */
const findMinicart = (root = document) => root.querySelector('#MinicartDrawer');

/**
 * Initializes the minicart by setting up event listeners and initializing line items.
 */
const initMinicart = () => {
  const minicart = findMinicart();
  if (!minicart) return;
  const privacyCheckbox = minicart.querySelector(
    '.privacy-checkbox__container',
  );
  if (
    privacyCheckbox &&
    typeof window.dAgency.initPrivacyCheckbox == 'function'
  )
    window.dAgency.initPrivacyCheckbox(privacyCheckbox);
  minicart.querySelectorAll('.cart-line__wrapper[data-key]').forEach((line) =>
    initLine({
      line,
      beforeFetch: () => {
        minicart.classList.add('loading');
      },
      afterFetch: () => {
        updateMinicart();
      },
    }),
  );
};

/**
 * Updates the minicart content by fetching the latest data and reinitializing the minicart.
 */
const updateMinicart = async () => {
  const minicart = findMinicart();
  if (!minicart) return;
  minicart.classList.add('loading');
  await fetch(minicart.dataset.refreshUrl)
    .then((res) => res.text())
    .then((data) => {
      const minicartSection = new DOMParser().parseFromString(
        data,
        'text/html',
      );
      const fetchMinicart = findMinicart(minicartSection);
      if (fetchMinicart) minicart.innerHTML = fetchMinicart.innerHTML;
      minicart
        .querySelectorAll('.modal__backdrop , .modal__close')
        .forEach((closer) =>
          closer.addEventListener('click', () => closeModal(minicart)),
        );
      initMinicart();
    });
  minicart.classList.remove('loading');
};

/**
 * Initializes a line item in the minicart by setting up event listeners for
 * quantity changes and item removal.
 * @param {Object} options - Options for initializing the line item.
 * @param {HTMLElement} options.line - The line item element.
 * @param {Function} [options.beforeFetch] - Function to call before sending the request.
 * @param {Function} [options.afterFetch] - Function to call after successfully changing the cart item.
 */
const initLine = ({ line, beforeFetch, afterFetch }) => {
  if (!line) return;
  const minusBtn = line.querySelector('.cart-line__quantity-btn--minus');
  const plusBtn = line.querySelector('.cart-line__quantity-btn--plus');
  const field = line.querySelector('.cart-line__quantity-value');

  if (minusBtn && plusBtn && field) {
    initNumberField({
      plusBtn,
      minusBtn,
      field,
    });

    field.addEventListener(
      'change',
      debounce(() => {
        changeCart({
          key: line.dataset.key,
          quantity: parseInt(field.value),
          callbacks: {
            beforeFetch: beforeFetch,
            afterFetch: afterFetch,
          },
        });
      }, 300),
    );
  }

  const remove = line.querySelector('.cart-line__remove');
  if (remove) {
    remove.addEventListener('click', (e) => {
      e.preventDefault();
      changeCart({
        key: line.dataset.key,
        quantity: 0,
        callbacks: {
          beforeFetch: beforeFetch,
          afterFetch: afterFetch,
        },
      });
    });
  }
};

/**
 * Opens the minicart, optionally loading its content before opening.
 *
 * @param {boolean} [loadBeforeOpen=false] - Whether to load the minicart content before opening it.
 */
const openMinicart = async (loadBeforeOpen = false) => {
  if (loadBeforeOpen) await updateMinicart();
  openModal(findMinicart());
};

const wishlistName = 'main_wishlist';

/**
 * Creates a new wishlist with the name specified in `wishlistName`.
 *
 * @returns {Promise<Object|boolean>} A promise that resolves to the wishlist object, or `false` if `window._swat` is not defined.
 * @throws {Error} Will throw an error if the network request fails or if the response is not ok.
 */
const createWishlist = () => {
  if (!window._swat) {
    console.error('window._swat is not defined');
    return false;
  }
  return new Promise((resolve, reject) => {
    window._swat.createList(
      {
        lname: wishlistName,
      },
      function (newList) {
        resolve(newList);
      },
      function (error) {
        reject(new Error('Error while creating a List: ', { cause: error }));
      },
    );
  });
};

/**
 * Retrieves the wishlist with the name specified by `wishlistName`.
 *
 * @returns {Promise<Object|boolean>} A promise that resolves to the wishlist object, or `false` if `window._swat` is not defined.
 * @throws {Error} Will throw an error if the network request fails or if the response is not ok.
 */
const getWishlist = () => {
  if (!window._swat) {
    console.error('window._swat is not defined');
    return false;
  }
  return new Promise((resolve, reject) => {
    window._swat.fetchLists({
      callbackFn: async function (lists) {
        const foundList = lists.find((list) => list.lname == wishlistName);
        if (foundList) resolve(foundList);
        const newlist = await createWishlist();
        resolve(newlist);
      },
      errorFn: function (error) {
        reject(
          new Error('Error while fetching all Lists: ', {
            cause: error,
          }),
        );
      },
    });
  });
};

/**
 * Adds a product to the wishlist.
 *
 * @param {Object} [options] - Options for adding the product to the wishlist.
 * @param {string} [options.variantID] - Unique variant ID of the product.
 * @param {string} [options.masterID] - Master product ID of the product.
 * @param {string} [options.productURL] - Product URL.
 *
 * @returns {Promise<Object|boolean>} A promise that resolves to the added product
 * object, or `false` if the product was not added. If there is an error, the promise
 * is rejected with an Error object.
 */
const addToWishlist = async (
  {
    variantID = '',
    masterID = '',
    productURL = '',
    compareAtPrice = '',
    price = '',
  } = {
    variantID: '',
    masterID: '',
    productURL: '',
    compareAtPrice: '',
    price: '',
  },
) => {
  if (!variantID || !masterID || !productURL) {
    console.error(
      'Trying to add product to wishlist with missing required parameters: variantID, masterID, productURL',
    );
    return false;
  }
  if (!window._swat) {
    console.error('window._swat is not defined');
    return false;
  }
  const wishlist = await getWishlist();

  if (wishlist.lid) {
    return new Promise(function (resolve, reject) {
      window._swat.addToList(
        wishlist.lid, // list id
        {
          epi: variantID, // Unique variant ID of the product
          empi: masterID, // Master Product Id of the product
          du: productURL, // Product URL
          cprops: { compare_at_price: compareAtPrice, price: price }, // Compare at price and price of the product
        },
        function (addedListItem) {
          resolve(addedListItem);
        },
        function (error) {
          reject(
            new Error('Error while adding product to wishlist: ', {
              cause: error,
            }),
          );
        },
      );
    });
  } else {
    console.error(
      'wishlist.lid is not defined while trying to add product to wishlist',
    );
    return false;
  }
};

/**
 * Deletes a product from the wishlist.
 *
 * @param {Object} [options] - Options for deleting the product from the wishlist.
 * @param {string} [options.variantID] - Unique variant ID of the product.
 * @param {string} [options.masterID] - Master product ID of the product.
 * @param {string} [options.productURL] - Product URL.
 *
 * @returns {Promise<Object|boolean>} A promise that resolves to the deleted product object, or `false` if the product was not deleted. If there is an error, the promise is rejected with an Error object.
 * @throws {Error} Will throw an error if the network request fails or if the response is not ok.
 */
const deleteFromWishlist = async (
  { variantID = '', masterID = '', productURL = '' } = {
    variantID: '',
    masterID: '',
    productURL: '',
  },
) => {
  if (!variantID || !masterID || !productURL) {
    console.error(
      'Trying to delete product from wishlist with missing required parameters: variantID, masterID, productURL',
    );
    return false;
  }

  if (!window._swat) {
    console.error('window._swat is not defined');
    return false;
  }

  const wishlist = await getWishlist();
  if (!wishlist) {
    return true;
  }

  if (wishlist.lid) {
    return new Promise(function (resolve, reject) {
      window._swat.deleteFromList(
        wishlist.lid, // list id
        {
          epi: variantID, // Unique variant ID of the product
          empi: masterID, // Master Product Id of the product
          du: productURL, // Product URL
        },
        function (deletedProduct) {
          resolve(deletedProduct);
        },
        function (error) {
          reject(
            new Error('Error while deleting product from wishlist: ', {
              cause: error,
            }),
          );
        },
      );
    });
  } else {
    console.warn(
      'wishlist.lid is not defined while trying to delete product from wishlist',
    );
    return false;
  }
};

/**
 * Updates the wishlist buttons by finding the corresponding product in the
 * wishlist and updating the `data-wishlist-status` attribute accordingly.
 *
 * @returns {Promise<void>}
 */
const updateWishlistBtns = async () => {
  const wishlist = await getWishlist();
  document.querySelectorAll('[data-wishlist-btn]').forEach((button) => {
    if (window._swat) {
      button.removeAttribute('data-wishlist-blocking');
    }
    if (
      button?.dataset?.wishlistStatus != 'active' &&
      button?.dataset?.wishlistStatus != 'inactive'
    ) {
      const product = wishlist?.listcontents?.find(
        (product) =>
          product.epi == button?.dataset?.wishlistVariantId &&
          product.empi == button?.dataset?.wishlistProductId,
      );
      button.dataset.wishlistStatus = product ? 'active' : 'inactive';
    }
  });
};

/**
 * Initializes the wishlist by adding the `updateWishlistBtns` function to the
 * global `window.SwymCallbacks` array. This array is used by the Swym app to
 * execute functions after the wishlist is loaded.
 */
const initFirstWishlit = () => {
  if (!window.SwymCallbacks) window.SwymCallbacks = [];
  window.SwymCallbacks.push(updateWishlistBtns);
};

if (!window.dAgency) window.dAgency = {};
window.dAgency.initProductTile = (context) => {
  const snippetContainer = context.classList.contains(
      '.product-tile__container',
  )
    ? context
    : context.closest('.product-tile__container');
  if (
    !snippetContainer ||
    snippetContainer.classList.contains('product-tile__container--initialized')
  )
    return;

  const swiperWrapper = snippetContainer.querySelector(
    '.product-tile__gallery .swiper-wrapper',
  );
  const barActions = snippetContainer.querySelector('.product-tile__options-actions');
  if (barActions) {
    barActions.querySelectorAll('button');
    let show_more = barActions.querySelector('button.show-more');
    let show_less = barActions.querySelector('button.show-less');

    show_more.addEventListener("click", function (e) {
      e.preventDefault();
      e.target.getAttribute('rel');
      snippetContainer.classList.add('show-tile-details');
    });

    show_less.addEventListener("click", function (e) {
      e.preventDefault();
      e.target.getAttribute('rel');
      snippetContainer.classList.remove('show-tile-details');
    });

    snippetContainer.addEventListener("mouseleave", function (e) {
      if (snippetContainer.classList.contains('show-tile-details')) {
        e.target.classList.remove('show-tile-details');
      }
    });
  }

  if (
    swiperWrapper &&
    swiperWrapper.querySelectorAll('.swiper-slide').length > 1
  ) {
    const swiperContainer = swiperWrapper.closest('.product-tile__gallery');
    const pagination = swiperContainer.querySelector(
      '.product-tile__gallery-pagination',
    );
    const options = {
      modules: [Navigation, ...(pagination ? [Pagination] : [])],
      navigation: {
        prevEl: swiperContainer.querySelector(
          '.product-tile__gallery-arrow--left',
        ),
        nextEl: swiperContainer.querySelector(
          '.product-tile__gallery-arrow--right',
        ),
      },
      ...(pagination ? {
          pagination: {
            el: pagination,
          },
        }: {}),
    };
    var changeSlide = true;
    const swiper = new Swiper(swiperContainer, options);
    swiperContainer.addEventListener('mouseenter', () => {
      if (window.innerWidth <= parseInt(breakpoints.lg)) return;
      if (changeSlide) swiper.slideNext();
      changeSlide = false;
    });
  }

  const quickBuy = snippetContainer.querySelector('.product-tile__quick-buy');
  const form = snippetContainer.querySelector('.product-tile__form');
  const wishlistButton = snippetContainer.querySelector('[data-wishlist-btn]');
  if (quickBuy && form)
    initProductForm({
      form,
      callbacks: {
        afterVariantChange: async ({
              variant,
              product
            }) => {
          if (wishlistButton) {
            wishlistButton.dataset.wishlistVariantId = variant?.id;
            wishlistButton.dataset.wishlistPrice = formatPrice({
              value: variant?.price ?? 0,
            });
            wishlistButton.dataset.wishlistCompareAtPrice =
              variant?.compare_at_price ?
                formatPrice({
                    value: variant?.compare_at_price,
              }):
              '';

            const wishlist = await getWishlist();
            const wishlistProduct = wishlist?.listcontents?.find(
              (addedProduct) =>
              addedProduct.epi == variant.id &&
                addedProduct.empi == product.id,
            );
            wishlistButton.dataset.wishlistStatus = wishlistProduct ?
              'active' :
              'inactive';
          }
        },
        afterSubmit: () => openMinicart(true),
      },
    });

  snippetContainer.classList.add('product-tile__container--initialized');

  if (wishlistButton) {
    const toggleLoading = throttle(() => {
      wishlistButton.classList.toggle('loading');
    }, 500);
    wishlistButton.addEventListener('click', async (e) => {
      e.preventDefault();
      toggleLoading();
      const variantID = wishlistButton?.dataset?.wishlistVariantId;
      const masterID = wishlistButton?.dataset?.wishlistProductId;
      const productURL = wishlistButton?.dataset?.wishlistProductUrl;
      const price = wishlistButton?.dataset?.wishlistPrice;
      const compareAtPrice = wishlistButton?.dataset?.wishlistCompareAtPrice;

      if (wishlistButton?.dataset?.wishlistStatus === 'active') {
        await deleteFromWishlist({
          variantID,
          masterID,
          productURL
        });
        wishlistButton.dataset.wishlistStatus = 'inactive';
      } else {
        await addToWishlist({
          variantID,
          masterID,
          productURL,
          compareAtPrice,
          price,
        });
        wishlistButton.dataset.wishlistStatus = 'active';
      }
      toggleLoading();
    });
  }
};

execOnLoad(window.dAgency.initProductTile, '.product-tile__container');

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

function setCookie(name, value, days) {
  const d = new Date();
  d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
  let expires = "expires=" + d.toUTCString();
  document.cookie = name + "=" + value + ";" + expires + ";path=/";
}

function addProductToRecentlyViewed(productHandle) {
  let recentlyViewed = getCookie('recently_viewed') ? JSON.parse(getCookie('recently_viewed')) : [];

  if (!recentlyViewed.includes(productHandle)) {
    recentlyViewed.push(productHandle);
  }

  if (recentlyViewed.length > 4) {
    recentlyViewed.shift();
  }

  setCookie('recently_viewed', JSON.stringify(recentlyViewed), 30);
}

function displayRecentlyViewed() {
  const recentlyViewed = getCookie('recently_viewed') ? JSON.parse(getCookie('recently_viewed')) : [];
  const activeLanguage = (typeof Shopify !== 'undefined' && Shopify.locale) || 'en';
  const section = document.querySelector('.recently-viewed__section');


  if (!section) {
    return;
  }
  if (recentlyViewed.length > 0) {
    section.style.display = 'block';
    const container = document.querySelector('.recently-viewed__container .recently-viewed__list-container');
    const dataLayerValue = container?.getAttribute('data-layer');
    container.innerHTML = '';

    recentlyViewed.forEach(handle => {
      fetch(`/products/${handle}.js`)
        .then(response => response.json())
        .then(product => {
          const soldOut = !product.variants.some(variant => variant.available);
          const firstVariant = product.variants[0];

          const mediaHTML = product.images.slice(0, 2).map((image, index) => `
            <div class="media__container product-tile__gallery-media-${index + 1}">
              ${dataLayerValue ? `
                <div
                  class="product-tile__gallery-layer"
                  style="background: ${dataLayerValue};">
                </div>
              ` : ''}
              <img
                src="${image}"
                alt="${product.title}"
                class="product-tile__gallery-media-${index + 1}"
              />
            </div>
          `).join('');

          const tagHTML = product.tags.map(tag => {
            if (tag.startsWith('label_')) {
              const processedTag = tag.replace('label_', '');
              const pairs = processedTag.split('|');
              for (const pair of pairs) {
                const parts = pair.split('-');
                if (parts[0] === activeLanguage) {
                  return `<div class="product-tile__tags-label">${parts[1].toUpperCase()}</div>`;
                }
              }
            }
            return '';
          }).find(Boolean) || '';

          const productHTML = `
            <div class="product-tile__container">
              <a href="${product.url}" class="product-tile__gallery">
                ${mediaHTML}
                <div class="product-tile__tags">
                  ${tagHTML}
                </div>
              </a>
              <div class="product-tile__info">
                ${product.vendor ? `<p class="product-tile__vendor">${product.vendor}</p>` : ''}
                <a href="${product.url}" class="product-tile__title">
                  <p>${product.title}</p>
                </a>
                ${
                  !soldOut
                    ? `
                      <div class="product-tile__price">
                        ${firstVariant.compare_at_price ? `<del>${(firstVariant.compare_at_price / 100).toFixed(2)}</del>` : ''}
                        <span>${(firstVariant.price / 100).toFixed(2)}</span>
                      </div>
                    `
                    : '<div class="product-tile__sold-out">Sold Out</div>'
                }
              </div>
            </div>
          `;
          container.insertAdjacentHTML('beforeend', productHTML);
        });
    });
  } else {
    section.style.display = 'none';
  }
}

document.addEventListener('DOMContentLoaded', function () {
  const productContainer = document.querySelector('.product-page__section');
  if (productContainer) {
    const url = window.location.pathname;
    const productHandle = url.split('/').pop();

    if (productHandle) {
      addProductToRecentlyViewed(productHandle);
    }
  }

  displayRecentlyViewed();
});

const initInputs = (root = document) => {
  if (!root) return;
  const rootEl = typeof root === 'string' ? document.querySelector(root) : root;
  if (!rootEl) return;

  rootEl.querySelectorAll('.form-element--icon').forEach((iconInput) => {
    const icon = iconInput.querySelector('.icon');
    const input = iconInput.querySelector('input');
    if (!icon || !input) return;

    if (input.type == 'password')
      icon.addEventListener('click', () => {
        iconInput.classList.toggle('active');
        input.type = input.type == 'password' ? 'text' : 'password';
      });
  });

  rootEl.querySelectorAll('.form-element--range').forEach((rangeWrapper) => {
    const rangeMin = rangeWrapper.querySelector('input[range-min]');
    const rangeMax = rangeWrapper.querySelector('input[range-max]');
    const rangeIndicator = rangeWrapper.querySelector('.range-indicator');

    if (!rangeIndicator) {
      console.error('Cannot initialize range input. Range indicator not found');
      return;
    }

    if (!rangeMin?.min || rangeMin?.min != rangeMax?.min) {
      console.error(
        'Cannot initialize range input. Range inputs have different min values',
      );
      return;
    }
    if (!rangeMin?.max || rangeMin?.max != rangeMax?.max) {
      console.error(
        'Cannot initialize range input. Range inputs have different max values',
      );
      return;
    }

    const range = parseInt(rangeMin.max) - parseInt(rangeMin.min);
    var minValue = parseInt(rangeMin.value);
    var maxValue = parseInt(rangeMax.value);
    const gap = parseInt(rangeMin?.dataset?.gap ?? rangeMax?.dataset?.gap) ?? 1;

    const updateIndicator = () => {
      const left = (minValue * 100) / range;
      const buffer = (maxValue * 100) / range;
      const right = 100 - buffer;
      rangeIndicator.style.left = `${left}%`;
      rangeIndicator.style.right = `${right}%`;
    };
    updateIndicator();

    rangeMin.addEventListener('input', () => {
      if (parseInt(rangeMin.value) >= maxValue - gap) {
        rangeMin.value = maxValue - gap;
        ['input', 'change'].forEach((evt) =>
          rangeMin.dispatchEvent(new Event(evt, { bubbles: true })),
        );
      }
      minValue = parseInt(rangeMin.value);
      updateIndicator();
    });

    rangeMax.addEventListener('input', () => {
      if (parseInt(rangeMax.value) <= minValue + gap) {
        rangeMax.value = minValue + gap;
        ['input', 'change'].forEach((evt) =>
          rangeMax.dispatchEvent(new Event(evt, { bubbles: true })),
        );
      }
      maxValue = parseInt(rangeMax.value);
      updateIndicator();
    });
  });
};

const getCssProp = (el, prop) => {
  if (!el || !prop) return;

  const value = getComputedStyle(el)[prop];
  if (value.indexOf('px') < 0) return;
  return parseFloat(value.replace('px', ''));
};

const calcElHeight = (el, includeMargin = true) => {
  if (!el) return;
  const borderTop = getCssProp(el, 'border-top-width');
  const borderBottom = getCssProp(el, 'border-bottom-width');
  const buffer = el.clientHeight + borderTop + borderBottom;
  if (!includeMargin) return buffer;
  const marginTop = getCssProp(el, 'margin-top');
  const marginBottom = getCssProp(el, 'margin-bottom');
  return buffer + marginTop + marginBottom;
};

const openAccordion = (details) => {
  if (!details || details.classList.contains('is-animating')) return;
  if (details.open) return;
  details.classList.add('is-animating');
  const content = details.querySelector(':scope > .accordion__content');
  if (!content) return;

  const startHeight = calcElHeight(details, false);
  details.style.height = `${startHeight}px`;
  details.style.overflow = 'hidden';
  details.open = true;
  window.requestAnimationFrame(() => {
    const endHeight = startHeight + calcElHeight(content);
    const animation = details.animate(
      {
        height: [`${startHeight}px`, `${endHeight}px`],
      },
      {
        duration: 800,
        easing: 'ease-out',
      },
    );
    animation.onfinish = () => {
      details.classList.remove('is-animating');
      details.style.height = '';
      details.style.overflow = '';
    };
  });
};

const closeAccordion = (details) => {
  if (!details || details.classList.contains('is-animating')) return;
  if (!details.open) return;
  details.classList.add('is-animating');
  const content = details.querySelector(':scope > .accordion__content');
  if (!content) return;

  const startHeight = calcElHeight(details, false);
  const endHeight = startHeight - calcElHeight(content);
  details.style.height = `${startHeight}px`;
  details.style.overflow = 'hidden';
  window.requestAnimationFrame(() => {
    const animation = details.animate(
      {
        height: [`${startHeight}px`, `${endHeight}px`],
      },
      {
        duration: 800,
        easing: 'ease-out',
      },
    );
    animation.onfinish = () => {
      details.classList.remove('is-animating');
      details.style.height = '';
      details.style.overflow = '';
      details.open = false;
    };
  });
};

const initAccordions = (root = document) => {
  if (!root) return;
  const rootEl = typeof root === 'string' ? document.querySelector(root) : root;
  if (!rootEl) return;

  rootEl.querySelectorAll('details').forEach((details) => {
    const summary = details.querySelector(':scope > summary');
    const content = details.querySelector(':scope > .accordion__content');
    if (!summary || !content) return;

    summary.addEventListener('click', (e) => {
      e.preventDefault();

      if (
        !details.open &&
        typeof details.dataset.exclusive !== 'undefined' &&
        details.dataset.index
      )
        document
          .querySelectorAll(
            `details:not([data-index="${details.dataset.index}"])${details.dataset.group ? `[data-group="${details.dataset.group}"]` : ''}`,
          )
          .forEach((details) => closeAccordion(details));

      details.open ? closeAccordion(details) : openAccordion(details);
    });
  });
};

const initVideoControls = (root = document) => {
  if (!root) return;
  const rootEl = typeof root === 'string' ? document.querySelector(root) : root;
  if (!rootEl) return;

  rootEl
    .querySelectorAll('.media__custom-controls')
    .forEach((customControls) => {
      const mediaContainer = customControls.closest('.media__container');
      if (!mediaContainer) return;

      const videos = mediaContainer.querySelectorAll('video');
      if (videos.length <= 0) return;

      const playControl = customControls.querySelector(
        '.media__custom-controls-play',
      );
      playControl.addEventListener('click', () => {
        if (mediaContainer.classList.contains('playing')) {
          mediaContainer.classList.remove('playing');
          videos.forEach((video) => video.pause());
        } else {
          mediaContainer.classList.add('playing');
          videos.forEach((video) => video.play());
        }
      });

      const audioControl = customControls.querySelector(
        '.media__custom-controls-audio',
      );
      audioControl.addEventListener('click', () => {
        const isMuted = videos[0]?.muted || false;
        videos.forEach((video) => {
          video.muted = !isMuted;
        });

        if (isMuted) {
          audioControl.classList.remove('muted');
          audioControl.classList.add('unmuted');
        } else {
          audioControl.classList.remove('unmuted');
          audioControl.classList.add('muted');
        }
      });
    });
};

(() => {
  initInputs();
  initAccordions();
  initModals();
  initMinicart();
  initVideoControls();
  initFirstWishlit();
})();

if (!window.dAgency) window.dAgency = {};
window.dAgency.initStyleguide = (context) => {
  const templateContainer = context.classList.contains('.styleguide__container')
    ? context
    : context.closest('.styleguide__container');
  if (!templateContainer) return;

  templateContainer
    .querySelectorAll('.styleguide__colors-box')
    .forEach((color) => {
      const example = color.querySelector('.styleguide__colors-box-example');
      const hex = color.querySelector('.styleguide__colors-box-hex');
      if (!example || !hex) return;

      hex.innerText = getComputedStyle(example).backgroundColor;
    });

  templateContainer
    .querySelectorAll(
      '.styleguide__breakpoints [name="active_breakpoint"][type="radio"]',
    )
    .forEach((radio) =>
      radio.addEventListener(
        'click',
        () => (templateContainer.style.maxWidth = radio.value),
      ),
    );

  const buttonsWrapper = templateContainer.querySelector(
    '.styleguide__buttons',
  );
  if (buttonsWrapper)
    buttonsWrapper
      .querySelectorAll('input[type="checkbox"]')
      .forEach((checkbox) =>
        checkbox.addEventListener('click', () =>
          buttonsWrapper.classList.toggle(
            `styleguide__buttons--${checkbox.name}`,
          ),
        ),
      );

  const spacingsWrapper = templateContainer.querySelector(
    '.styleguide__spacings',
  );
  if (spacingsWrapper) {
    const columnsSelect = spacingsWrapper.querySelector(
      'select[name="columns"]',
    );
    const columnsGrid = spacingsWrapper.querySelector(
      '.styleguide__spacings-columns-grid[data-cols]',
    );
    if (columnsSelect && columnsGrid)
      columnsSelect.addEventListener(
        'change',
        () => (columnsGrid.dataset.cols = columnsSelect.value),
      );
  }
};

execOnLoad(window.dAgency.initStyleguide, '.styleguide__container');

if (!window.dAgency) window.dAgency = {};
window.dAgency.initAnnouncementBar = (context) => {
  const sectionContainer = context.classList.contains('.shopify-section')
    ? context
    : context.closest('.shopify-section');
  if (!sectionContainer) return;
  const innerContainer = sectionContainer.querySelector(
    '.announcement-bar__container',
  );
  const backgroundContainer = sectionContainer.querySelector(
    '.announcement-bar__background',
  );
  if (!innerContainer || !backgroundContainer) return;

  const toggleAnnouncement = (close = true) => {
    var startHeight = '0px';
    var endHeight = `${innerContainer.clientHeight}px`;

    if (close) {
      startHeight = `${innerContainer.clientHeight}px`;
      endHeight = '0px';
      Cookies.set('announcement-closed', 'true', { expires: 7 });
    }
    const animation = sectionContainer.animate(
      {
        height: [startHeight, endHeight],
      },
      {
        duration: 300,
        easing: 'ease-out',
      },
    );
    animation.onfinish = () => {
      backgroundContainer.classList.toggle('hidden');
      resizeCallback();
    };
  };

  const closeBtn = sectionContainer.querySelector('.announcement-bar__close');
  if (closeBtn) {
    closeBtn.addEventListener('click', toggleAnnouncement);
    const announcementClosed = Cookies.get('announcement-closed');
    if (announcementClosed !== 'true') toggleAnnouncement(false);
  }

  const resizeCallback = throttle(
    () =>
      document.documentElement.style.setProperty(
        '--announcement-bar-height',
        `${sectionContainer.clientHeight}px`,
      ),
    500,
  );
  resizeCallback();
  window.addEventListener('resize', resizeCallback);

  if (
    innerContainer.classList.contains('announcement-bar__container--carousel')
  ) {
    const prevArrow = sectionContainer.querySelector(
      '.announcement-bar__arrow--left',
    );
    const nextArrow = sectionContainer.querySelector(
      '.announcement-bar__arrow--right',
    );

    const options = {
      modules: [Autoplay, ...(prevArrow && nextArrow ? [Navigation] : [])],
      loop: innerContainer.querySelectorAll('.swiper-slide').length >= 2,
      direction: innerContainer.dataset?.scrollDirection ?? 'horizontal',
      autoplay: {
        enabled: true,
        delay: parseFloat(innerContainer.dataset.animationSpeed) * 1000,
      },
      ...(prevArrow && nextArrow
        ? {
            navigation: {
              prevEl: prevArrow,
              nextEl: nextArrow,
            },
          }
        : {}),
    };

    new Swiper(innerContainer, options);
  } else if (
    innerContainer.classList.contains('announcement-bar__container--marquee')
  ) {
    const messagesWrapper = innerContainer.querySelector(
      '.announcement-bar__messages-wrapper',
    );
    if (!messagesWrapper) return;
    const messagesAccumulator = messagesWrapper.parentElement;
    const duplicationCount =
      Math.ceil(innerContainer.clientWidth / messagesWrapper.clientWidth) * 2;
    messagesAccumulator.appendChild(messagesWrapper.cloneNode(true));

    for (var i = 0; i < duplicationCount; i++) {
      messagesAccumulator.appendChild(messagesWrapper.cloneNode(true));
      messagesAccumulator.appendChild(messagesWrapper.cloneNode(true));
    }
  }
};

execOnLoad(window.dAgency.initAnnouncementBar, '.announcement-bar__section');

/**
 * Fetches the text content of a given URL.
 *
 * @param {string} url - The URL to fetch content from.
 * @returns {Promise<string|false>} The text content from the fetched URL, or false if the URL is not provided.
 */
const fetchPageText = (url) => {
  if (!url) return false;
  return fetch(url).then((res) => res.text());
};

/**
 * Selects elements based on provided selectors from the given HTML string or DOM.
 *
 * @param {Object} params - The parameters object.
 * @param {string} [params.data] - HTML string to parse.
 * @param {Document} [params.dom] - DOM document to use for querying.
 * @param {Object} params.selectors - Selectors for the elements to select.
 * @param {string} params.selectors.parent - The selector for the parent element.
 * @param {string} [params.selectors.counter='[data-pagination-counter]'] - The selector for the counter element.
 * @param {string} [params.selectors.loadButton='.pagination__load-more'] - The selector for the load button.
 * @param {string} [params.selectors.contentWrapper='.pagination__content-wrapper'] - The selector for the content wrapper.
 * @returns {Object} The selected elements: `counterEl`, `loadButtonEl`, and `contentWrapperEl`.
 */
const selectElements = ({
  data,
  dom,
  selectors: {
    parent,
    counter = '[data-pagination-counter]',
    loadButton = '.pagination__load-more',
    contentWrapper = '.pagination__content-wrapper',
  },
}) => {
  if (!data && !dom) return {};
  const domEl = dom ? dom : new DOMParser().parseFromString(data, 'text/html');
  const parentEl = parent ? domEl.querySelector(parent) : domEl;
  return {
    counterEl: parentEl.querySelector(counter),
    loadButtonEl: parentEl.querySelector(loadButton),
    contentWrapperEl: parentEl.querySelector(contentWrapper),
  };
};

/**
 * Updates the current browser URL using the History API.
 *
 * @param {string} targetUrl - The target URL to update to.
 */
const updateUrl = (targetUrl) => {
  history.replaceState(
    null,
    '',
    targetUrl.indexOf(window.location.origin) >= 0
      ? targetUrl
      : `${window.location.origin}${targetUrl}`,
  );
};

/**
 * Updates the content of the page by inserting new content before or after existing content.
 *
 * @param {Object} params - The parameters object.
 * @param {boolean} [params.insertAfter=true] - Whether to insert the new content after the existing content.
 * @param {Promise<string>} params.data - The HTML string to insert.
 * @param {Object} params.components - The current DOM components.
 * @param {HTMLElement} params.components.counterEl - The counter element.
 * @param {HTMLElement} params.components.loadButtonEl - The load button element.
 * @param {HTMLElement} params.components.contentWrapperEl - The content wrapper element.
 * @param {Object} params.selectors - The selectors for various elements.
 * @param {string} params.selectors.parent - The selector for the parent element.
 * @param {string} params.selectors.counter - The selector for the counter element.
 * @param {string} params.selectors.loadButton - The selector for the load button.
 * @param {string} params.selectors.contentWrapper - The selector for the content wrapper.
 * @param {Object} params.callbacks - The callback functions.
 * @param {Function} [params.callbacks.beforeFetch] - The function to call before fetching the new content.
 * @param {Function} [params.callbacks.afterFetch] - The function to call after fetching and updating the content.
 */
const updateContent = async ({
  insertAfter = true,
  data,
  components: { counterEl = false, loadButtonEl, contentWrapperEl },
  selectors: { parent, counter, loadButton, contentWrapper },
  callbacks: { beforeFetch, afterFetch },
} = {}) => {
  if (typeof beforeFetch == 'function')
    beforeFetch({
      insertAfter,
      components: { counterEl, loadButtonEl, contentWrapperEl },
    });
  const {
    counterEl: newCounterEl,
    loadButtonEl: newLoadButtonEl,
    contentWrapperEl: newContentWrapperEl,
  } = selectElements({
    data: await data,
    selectors: { parent, counter, loadButton, contentWrapper },
  });

  if (!newLoadButtonEl || !newContentWrapperEl) return;
  if (insertAfter) {
    updateUrl(loadButtonEl.dataset.nextUrl);
    if (newCounterEl && counterEl) counterEl.innerText = newCounterEl.innerText;
    loadButtonEl.dataset.nextUrl = newLoadButtonEl.dataset.nextUrl;
    contentWrapperEl.append(...Array.from(newContentWrapperEl.children));
  } else {
    loadButtonEl.dataset.previousUrl = newLoadButtonEl.dataset.previousUrl;
    contentWrapperEl.prepend(...Array.from(newContentWrapperEl.children));
  }

  if (typeof afterFetch == 'function')
    afterFetch({
      insertAfter,
      components: { counterEl, loadButtonEl, contentWrapperEl },
    });
};

/**
 * Reloads the previous pages of content by fetching and prepending them to the current content.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.components - The current DOM components.
 * @param {HTMLElement} params.components.loadButtonEl - The load button element.
 * @param {HTMLElement} params.components.contentWrapperEl - The content wrapper element.
 * @param {Object} params.selectors - The selectors for various elements.
 * @param {string} params.selectors.parent - The selector for the parent element.
 * @param {string} params.selectors.loadButton - The selector for the load button.
 * @param {string} params.selectors.contentWrapper - The selector for the content wrapper.
 * @param {Object} params.callbacks - The callback functions.
 * @param {Function} [params.callbacks.beforeFetch] - The function to call before fetching the new content.
 * @param {Function} [params.callbacks.afterFetch] - The function to call after fetching and updating the content.
 */
const reloadPreviousPages = async ({
  components: { loadButtonEl, contentWrapperEl },
  selectors: { parent, loadButton, contentWrapper },
  callbacks: { beforeFetch, afterFetch },
}) => {
  if (!loadButtonEl || !contentWrapperEl || !loadButtonEl?.dataset?.previousUrl)
    return;
  await updateContent({
    insertAfter: false,
    data: fetchPageText(loadButtonEl.dataset.previousUrl),
    components: { loadButtonEl, contentWrapperEl },
    selectors: { parent, loadButton, contentWrapper },
    callbacks: { beforeFetch, afterFetch },
  });
  reloadPreviousPages({
    components: { loadButtonEl, contentWrapperEl },
    selectors: { parent, loadButton, contentWrapper },
    callbacks: { beforeFetch, afterFetch },
  });
};

/**
 * Initializes the pagination functionality on the page.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.selectors - The selectors for various elements.
 * @param {string} params.selectors.parent - The selector for the parent element.
 * @param {string} [params.selectors.counter='[data-pagination-counter]'] - The selector for the counter element.
 * @param {string} [params.selectors.loadButton='.pagination__load-more'] - The selector for the load button.
 * @param {string} [params.selectors.contentWrapper='.pagination__content-wrapper'] - The selector for the content wrapper.
 * @param {Object} params.callbacks - The callback functions.
 * @param {Function} [params.callbacks.beforeFetch] - The function to call before fetching the new content.
 * @param {Function} [params.callbacks.afterFetch] - The function to call after fetching and updating the content.
 */
const initPagination = ({
  selectors: {
    parent,
    counter = '[data-pagination-counter]',
    loadButton = '.pagination__load-more',
    contentWrapper = '.pagination__content-wrapper',
  } = {
    counter: '[data-pagination-counter]',
    loadButton: '.pagination__load-more',
    contentWrapper: '.pagination__content-wrapper',
  },
  callbacks: { beforeFetch, afterFetch } = {},
} = {}) => {
  const { counterEl, loadButtonEl, contentWrapperEl } = selectElements({
    dom: document,
    selectors: { parent, counter, loadButton, contentWrapper },
  });

  if (!loadButtonEl || !contentWrapperEl) {
    console.error(
      'Cannot initialize pagination, the load button or content wrapper is missing or not found.',
    );
    return;
  }

  reloadPreviousPages({
    components: { loadButtonEl, contentWrapperEl },
    selectors: { parent, loadButton, contentWrapper },
    callbacks: { beforeFetch, afterFetch },
  });

  var nextPage = fetchPageText(loadButtonEl.dataset.nextUrl);
  const loadMore = async () => {
    loadButtonEl.disabled = true;
    loadButtonEl.classList.add('loading');
    await updateContent({
      insertAfter: true,
      data: nextPage,
      components: { counterEl, loadButtonEl, contentWrapperEl },
      selectors: { parent, counter, loadButton, contentWrapper },
      callbacks: { beforeFetch, afterFetch },
    });
    nextPage = fetchPageText(loadButtonEl.dataset.nextUrl);
    loadButtonEl.classList.remove('loading');
    loadButtonEl.disabled = false;
  };

  if (loadButtonEl.dataset.type == 'scroll') {
    const isBtnInPage = () => {
      if (!loadButtonEl.dataset.nextUrl) return false;
      const { y } = loadButtonEl.getBoundingClientRect();
      return y < window.innerHeight;
    };
    const loadScroll = throttle(async () => {
      if (!isBtnInPage()) return;
      await loadMore();
      loadScroll();
    }, 300);
    loadScroll();
    window.addEventListener('scroll', loadScroll);
  } else {
    loadButtonEl.addEventListener('click', async (e) => {
      e.preventDefault();
      loadMore();
    });
  }
};

if (!window.dAgency) window.dAgency = {};
window.dAgency.initBlogPage = (context) => {
  const sectionContainer = context.classList.contains('.shopify-section')
    ? context
    : context.closest('.shopify-section');
  if (!sectionContainer) return;

  initPagination({
    selectors: {
      contentWrapper: '.blog-page__grid',
    },
  });
};

execOnLoad(window.dAgency.initBlogPage, '.blog-page__section');

if (!window.dAgency) window.dAgency = {};
window.dAgency.initCartPage = (context) => {
  const sectionContainer = context.classList.contains('.shopify-section')
    ? context
    : context.closest('.shopify-section');
  if (!sectionContainer) return;

  const animationDuration = 350;
  const cartTotal = sectionContainer.querySelector('[data-items-subtotal]');
  const cartOriginal = sectionContainer.querySelector('[data-items-original]');
  const submit = sectionContainer.querySelector('[type="submit"]');
  const freeShipping = sectionContainer.querySelector(
    '.cart-page__free-shipping',
  );
  const emptyCart = sectionContainer.querySelector('.cart-page__empty');
  const updateCart = (cart) => {
    sectionContainer
      .querySelectorAll('.cart-line__wrapper[data-id]')
      .forEach((line) => {
        const item = cart.items.find((item) => item.id == line.dataset.id);
        if (item) {
          line.dataset.key = item.key;
          const price = line.querySelector('.cart-line__total > span');
          if (price)
            price.innerText = formatPrice({
              value: item.final_line_price,
            });

          const discountedPrice = line.querySelector('.cart-line__total > del');
          if (discountedPrice)
            discountedPrice.innerText =
              item.original_line_price != item.final_line_price
                ? formatPrice({
                    value: item.original_line_price,
                  })
                : '';

          const discountsWrapper = line.querySelector(
            '.cart-line__total-discounts',
          );
          if (discountsWrapper) {
            discountsWrapper.innerHTML = '';
            item.line_level_discount_allocations.forEach(
              (discountAllocations) => {
                const title = discountAllocations?.discount_application?.title;
                if (!title) return;
                const discountTitle = document.createElement('span');
                discountTitle.innerText = title;
                discountsWrapper.appendChild(discountTitle);
              },
            );
          }
        } else {
          line.style.height = `${line.clientHeight}px`;
          line.style.transition = `height ${animationDuration}ms`;
          window.requestAnimationFrame(() => {
            line.style.height = '0px';
            setTimeout(() => {
              line.remove();
            }, animationDuration);
          });
        }
        line.classList.remove('loading');
      });

    if (cartTotal)
      cartTotal.innerText = formatPrice({
        value: cart.items_subtotal_price,
      });
    if (cartOriginal) {
      const originalWrapper = cartOriginal.closest('.cart-page__totals-value');
      if (originalWrapper) {
        if (cart.original_total_price != cart.items_subtotal_price)
          originalWrapper.classList.remove('hidden');
        else originalWrapper.classList.add('hidden');
      }
      cartOriginal.innerText =
        cart.original_total_price != cart.items_subtotal_price
          ? formatPrice({
              value: cart.original_total_price,
            })
          : '';
    }
    if (submit) submit.disabled = cart.item_count <= 0;
    if (emptyCart) {
      if (cart.item_count <= 0) {
        emptyCart.classList.remove('hidden');
      } else {
        emptyCart.classList.add('hidden');
      }
    }

    if (freeShipping) {
      const bar = freeShipping.querySelector(
        '.cart-page__free-shipping-bar[data-threshold]',
      );
      const progress = freeShipping.querySelector(
        '.cart-page__free-shipping-bar-progress',
      );
      const incomplete = freeShipping.querySelector('[data-threshold-missing]');

      if (bar && progress && incomplete) {
        const threshold = parseInt(bar.dataset.threshold);
        const remaining = threshold - cart.items_subtotal_price;
        const progressPercentage =
          remaining > 0 ? (cart.items_subtotal_price * 100) / threshold : 100;
        bar.dataset.progress = progressPercentage;
        progress.style.width = `${progressPercentage}%`;
        incomplete.innerText = formatPrice({ value: remaining });
      }
    }
  };

  sectionContainer
    .querySelectorAll('.cart-line__wrapper[data-id]')
    .forEach((line) =>
      initLine({
        line,
        beforeFetch: () => {
          line.classList.add('loading');
        },
        afterFetch: updateCart,
      }),
    );
};

execOnLoad(window.dAgency.initCartPage, '.cart-page__section');

if (!window.dAgency) window.dAgency = {};
window.dAgency.initCollectionPage = (context) => {
  const sectionContainer = context.classList.contains('.shopify-section')
    ? context
    : context.closest('.shopify-section');
  if (!sectionContainer) return;

  initPagination({
    callbacks: {
      afterFetch: ({ components: { contentWrapperEl } }) => {
        if (typeof window?.dAgency?.initProductTile === 'function')
          contentWrapperEl
            .querySelectorAll('.product-tile__container')
            .forEach((tile) => window.dAgency.initProductTile(tile));
        updateWishlistBtns();
      },
    },
  });

  const productsContent = sectionContainer.querySelector(
    '.collection-page__content',
  );
  if (productsContent)
    sectionContainer
      .querySelectorAll(
        '.collection-page__view-switch-value[data-columns][data-device]',
      )
      .forEach((viewChange) =>
        viewChange.addEventListener('click', (e) => {
          e.preventDefault();
          productsContent.setAttribute(
            `data-columns-${viewChange.dataset.device}`,
            viewChange.dataset.columns,
          );
          sectionContainer
            .querySelectorAll(
              `.collection-page__view-switch-value[data-columns][data-device="${viewChange.dataset.device}"]`,
            )
            .forEach(
              (selectedView) =>
                (selectedView.dataset.active =
                  selectedView.dataset.columns == viewChange.dataset.columns &&
                  selectedView.dataset.device == viewChange.dataset.device),
            );
        }),
      );
};

execOnLoad(window.dAgency.initCollectionPage, '.collection-page__section');

if (!window.dAgency) window.dAgency = {};
window.dAgency.initSlideshow = (context) => {
  const sectionContainer = context.classList.contains('.shopify-section')
    ? context
    : context.closest('.shopify-section');
  if (!sectionContainer) return;

  const containers = context.querySelectorAll('.designers__list-container');

  containers.forEach((container) => {
    const items = Array.from(
      container.querySelectorAll('.designers__brand-name'),
    );
    const grouped = items
      .map((item) => {
        const text = item.textContent.trim();
        const firstChar = text[0].toUpperCase();
        const group = /^[0-9]/.test(firstChar) ? '1.' : firstChar;
        return { element: item, text, group };
      })
      .sort((a, b) => a.text.localeCompare(b.text))
      .reduce((acc, { element, group }) => {
        acc[group] = acc[group] || [];
        acc[group].push(element);
        return acc;
      }, {});

    const sortedGroups = Object.keys(grouped).sort((a, b) => {
      if (a === '1.') return -1;
      if (b === '1.') return 1;
      return a.localeCompare(b);
    });

    container.innerHTML = '';
    sortedGroups.forEach((group) => {
      const groupBlock = document.createElement('div');
      groupBlock.classList.add('designers__group');

      const groupTitle = document.createElement('div');
      groupTitle.classList.add('designers__letter');
      groupTitle.textContent = group;
      groupBlock.appendChild(groupTitle);

      const brandContainer = document.createElement('div');
      brandContainer.classList.add('designers__brand-container');

      grouped[group].forEach((item) => brandContainer.appendChild(item));
      groupBlock.appendChild(brandContainer);

      container.appendChild(groupBlock);
    });
  });

  const buttons = context.querySelectorAll('.designers__cta .btn');

  if (buttons) {
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const targetList = button.getAttribute('data-list');

        buttons.forEach((insideButton) => {
          var insideButtonList = insideButton.getAttribute('data-list');
          if (targetList == insideButtonList) {
            insideButton.classList.add('active');
          } else {
            insideButton.classList.remove('active');
          }
        });

        containers.forEach((container) => {
          if (container.getAttribute('data-list') === targetList) {
            container.style.display = 'block';
          } else {
            container.style.display = 'none';
          }
        });
      });
    });
  }
};

execOnLoad(window.dAgency.initSlideshow, '.designers__section');

if (!window.dAgency) window.dAgency = {};
window.dAgency.initHeader = (context) => {
  const sectionContainer = context.classList.contains('.shopify-section')
    ? context
    : context.closest('.shopify-section');
  if (!sectionContainer) return;

  const backgroundContainer = sectionContainer.querySelector(
    '.header__background',
  );

  const announcementBarHeight = document.documentElement.style.getPropertyValue(
    '--announcement-bar-height',
  );
  const scrollOffset = announcementBarHeight
    ? parseInt(announcementBarHeight.replace('px', ''))
    : 0;

  if (backgroundContainer) {
    const resizeCallback = throttle(
      () =>
        document.documentElement.style.setProperty(
          '--header-height',
          `${backgroundContainer.clientHeight}px`,
        ),
      500,
    );
    resizeCallback();
    window.addEventListener('resize', resizeCallback);

    if (typeof backgroundContainer.dataset.transparent !== 'undefined') {
      const scrollCallback = throttle(() => {
        if (sectionContainer.querySelector('.mobile-menu__container.open'))
          return;
        backgroundContainer.dataset.transparent =
          window.scrollY <= scrollOffset * 2;
      }, 500);
      scrollCallback();
      window.addEventListener('scroll', scrollCallback);
    }
  }

  const cartIcon = sectionContainer.querySelector('.header__action--cart');
  const minicart = document.querySelector('#MinicartDrawer');
  if (cartIcon && minicart)
    cartIcon.addEventListener('click', (e) => {
      e.preventDefault();
      openModal(minicart);
    });

  const cartCounter = sectionContainer.querySelector(
    '.header__action-cart-counter',
  );
  if (cartCounter) {
    document.addEventListener(afterCartChangeType, async () => {
      const cart = await getCart();
      if (cart) {
        cartCounter.innerText = cart.item_count;

        if (cart.item_count > 0) {
          cartCounter.style.display = 'flex';
        } else {
          cartCounter.style.display = 'none';
        }
      }
    });
  }

  const searchWrapper = sectionContainer.querySelector('.header__search');
  const searchIcon = sectionContainer.querySelector('.header__action--search');
  if (searchWrapper && searchIcon) {
    const mainInput = searchWrapper.querySelector('input[name="q"]');
    const closeWrapper = searchWrapper.querySelector('.header__search-close');
    if (mainInput && closeWrapper) {
      const closeSearch = () => {
        backgroundContainer.dataset.searchOpen = 'false';
        mainInput.value = '';
        mainInput.dispatchEvent(new Event('input', { bubbles: true }));
      };
      closeWrapper.addEventListener('click', closeSearch);
      searchIcon.addEventListener('click', (e) => {
        e.preventDefault();
        if (backgroundContainer.dataset.searchOpen == 'true') {
          closeSearch();
        } else {
          backgroundContainer.dataset.searchOpen = 'true';
          mainInput.focus();

          const openMobileMenu = sectionContainer.querySelector(
            '.mobile-menu__container',
          );
          if (openMobileMenu) {
            const modal = openMobileMenu.querySelector('.modal');
            openMobileMenu.classList.remove('open');
            closeModal(modal);
          }
        }
      });
    }
  }

  if (window.innerWidth >= parseInt(breakpoints.lg)) {
    const bigLogo = context.querySelector('.header__big-logo');

    if (bigLogo) {
      const allSections = document.querySelectorAll('main .shopify-section');
      const firstSection = allSections[0];
      const smallLogo = context.querySelector('.header__logo');

      allSections.forEach((section, index) => {
        if (index !== 0) {
          section.style.position = 'relative';
          section.style.zIndex = 2;
        }
      });

      if (firstSection) {
        firstSection.insertAdjacentElement('afterbegin', bigLogo);
        bigLogo.style.opacity = 1;
      }

      function handleLogoScroll() {
        const scrolledPast = window.scrollY > window.innerHeight * 1.3;

        if (scrolledPast) {
          bigLogo.style.opacity = 0;
          bigLogo.classList.remove('active');
          bigLogo.style.pointerEvents = 'none';
        } else {
          bigLogo.style.opacity = 1;
          bigLogo.classList.add('active');
          bigLogo.style.pointerEvents = 'auto';
        }

        smallLogo.classList.toggle('hidden', !scrolledPast);
      }

      window.addEventListener('scroll', handleLogoScroll);
      handleLogoScroll();
    }
  }

  let lastScrollPosition = 0;

  window.addEventListener('scroll', () => {
    const currentScrollPosition = window.scrollY;
    const plpStrip = document.querySelector('.collection-page__strip-wrapper');
    const searchStrip = document.querySelector(
      '.search-page__container .search-bar__container',
    );
    const legalStrip = document.querySelector(
      '.accordion.legal-page__navigation',
    );
    const translateYValue = `${backgroundContainer.clientHeight}px`;

    const isScrollingDown = currentScrollPosition > lastScrollPosition;
    const isInOffsetRange = currentScrollPosition <= scrollOffset * 1.5;

    if (isInOffsetRange) {
      backgroundContainer.style.transform = 'translateY(0)';
      if (plpStrip) {
        plpStrip.style.top = translateYValue;
      }
      if (searchStrip) {
        searchStrip.style.top = translateYValue;
      }
      if (legalStrip) {
        legalStrip.style.top = translateYValue;
      }
    } else {
      backgroundContainer.style.transform = isScrollingDown
        ? `translateY(-${translateYValue})`
        : 'translateY(0)';
      if (plpStrip) {
        plpStrip.style.top = isScrollingDown ? '0px' : translateYValue;
        plpStrip.style.zIndex = isScrollingDown ? '3' : '1';
      }
      if (searchStrip) {
        searchStrip.style.top = isScrollingDown ? '0px' : translateYValue;
      }
      if (legalStrip) {
        legalStrip.style.top = isScrollingDown ? '0px' : translateYValue;
      }
    }

    lastScrollPosition = currentScrollPosition;
  });
};

execOnLoad(window.dAgency.initHeader, '.header__section');

if (!window.dAgency) window.dAgency = {};
window.dAgency.initLegalPage = (context) => {
  const sectionContainer = context.classList.contains('.shopify-section')
    ? context
    : context.closest('.shopify-section');
  if (!sectionContainer) return;

  const submit = sectionContainer.querySelector('[type="submit"]');
  sectionContainer.querySelectorAll('input, textarea').forEach((input) => {
    const formElement = input.closest('.form-element');
    if (!formElement) return;

    input.addEventListener('invalid', (e) => {
      e.preventDefault();
      formElement.classList.add('error');
      if (submit) submit.disabled = true;
    });

    input.addEventListener('change', () => {
      formElement.classList.remove('error');
      const errorElements = sectionContainer.querySelectorAll(
        '.form-element.error',
      ).length;
      if (submit && errorElements === 0) submit.disabled = false;
    });
  });

  const navigation = sectionContainer.querySelector(
    'details.legal-page__navigation',
  );
  if (navigation) {
    const lgUp = window.matchMedia(`(min-width: ${breakpoints.lg})`);
    const handleNavigation = () => {
      navigation.open = lgUp.matches;
    };
    handleNavigation();
    lgUp.addEventListener('change', handleNavigation);
  }
};

execOnLoad(window.dAgency.initLegalPage, '.legal-page__section');

if (!window.dAgency) window.dAgency = {};
window.dAgency.initLocalizationSelector = (context) => {
  const sectionContainer = context.classList.contains('.shopify-section')
    ? context
    : context.closest('.shopify-section');
  if (!sectionContainer) return;

  const searchInput = sectionContainer.querySelector(
    '.localization-selector__search input',
  );
  if (!searchInput) return;

  const listItems = sectionContainer.querySelectorAll(
    '.localization-selector__item[data-search-info]',
  );
  const searchElement = debounce(() => {
    listItems.forEach((item) => {
      if (!searchInput.value) {
        item.style.display = '';
        return;
      }

      const searchValue = searchInput.value.toLowerCase();
      const searchData = item.dataset.searchInfo.toLowerCase().split('|');
      const isValueInData = searchData.some(
        (data) => data.indexOf(searchValue) >= 0,
      );
      item.style.display = isValueInData ? '' : 'none';
    });
  }, 300);
  searchInput.addEventListener('input', searchElement);
};

execOnLoad(
  window.dAgency.initLocalizationSelector,
  '.localization-selector__section',
);

if (!window.dAgency) window.dAgency = {};
window.dAgency.initLook = (context) => {
  const sectionContainer = context.classList.contains('.shopify-section')
    ? context
    : context.closest('.shopify-section');
  if (!sectionContainer) return;

  const sliderContainer = sectionContainer.querySelector('.look__carousel');
  if (!sliderContainer) return;

  new Swiper(sliderContainer, {
    loop: true,
    modules: [Navigation, Pagination],
    navigation: {
      prevEl: sectionContainer.querySelector('.look__carousel-arrow--left'),
      nextEl: sectionContainer.querySelector('.look__carousel-arrow--right'),
    },
    pagination: {
      el: sectionContainer.querySelector('.look__carousel-pagination'),
      clickable: true,
    },
  });
};

execOnLoad(window.dAgency.initLook, '.look__section');

if (!window.dAgency) window.dAgency = {};
window.dAgency.initNotFoundPage = (context) => {
  const sectionContainer = context.classList.contains('.shopify-section')
    ? context
    : context.closest('.shopify-section');
  if (!sectionContainer) return;

  const container = sectionContainer.querySelector(
    '.not-found-page__container',
  );
  if (!container) return;

  const query = window.location.pathname
    .replace(window.Shopify.routes.root, '')
    .split('/')
    .pop();
  fetch(
    `${window.Shopify.routes.root}search/suggest?q=${query}&resources[type]=product,collection&section_id=not-found-page`,
  )
    .then((res) => res.text())
    .then((data) => {
      const sectionDom = new DOMParser().parseFromString(data, 'text/html');
      const carousel = sectionDom.querySelector('.carousel__container');
      if (!carousel) return;
      container.appendChild(carousel);
      if (typeof window?.dAgency?.initCarousel == 'function')
        window.dAgency.initCarousel(carousel);

      if (typeof window?.dAgency?.initProductTile == 'function')
        carousel
          .querySelectorAll('.product-tile__container')
          .forEach((tile) => window.dAgency.initProductTile(tile));
    });
};

execOnLoad(window.dAgency.initNotFoundPage, '.not-found-page__section');

if (!window.dAgency) window.dAgency = {};
window.dAgency.initPasswordPage = (context) => {
  const sectionContainer = context.classList.contains('.shopify-section')
    ? context
    : context.closest('.shopify-section');
  if (!sectionContainer) return;

  const showButton = sectionContainer.querySelector('.password-page__toggle');
  const container = sectionContainer.querySelector('.password-page__container');
  if (showButton && container)
    showButton.addEventListener('click', () =>
      container.classList.toggle('password-page__container--show-form'),
    );
};

execOnLoad(window.dAgency.initPasswordPage, '.password-page__section');

if (!window.dAgency) window.dAgency = {};
window.dAgency.initPopup = (context) => {
  const sectionContainer = context.classList.contains('.shopify-section')
    ? context
    : context.closest('.shopify-section');
  if (!sectionContainer) return;

  const modal = sectionContainer.querySelector(
    '.modal[data-delay][data-cookie-duration]',
  );
  if (!modal) return;

  const delay = parseInt(modal.dataset.delay) * 1000;
  const cookieDuration = parseInt(modal.dataset.cookieDuration);
  const id = modal.id;
  const cookie = `modal-${id}-closed`;

  setTimeout(() => {
    if (Cookies.get(cookie) !== 'true') openModal(`#${id}`);
  }, delay);

  modal
    .querySelectorAll('.modal__backdrop , .modal__close')
    .forEach((closer) =>
      closer.addEventListener('click', () =>
        Cookies.set(cookie, 'true', { expires: cookieDuration }),
      ),
    );
};

execOnLoad(window.dAgency.initPopup, '.popup__section');

/**
 * Fetches omnibus data from FiloBlu.
 *
 * @param {Object} params - Parameters for the request.
 * @param {string} [params.brandKey] - Omnibus brand key. Defaults to `window.dAgency.strings.omnibusBrandKey`.
 * @param {string} [params.sku] - SKU of the product. Defaults to an empty string.
 * @param {string} [params.currency] - Currency code. Defaults to an empty string.
 * @param {Object} [params.callbacks] - Optional callback functions.
 * @param {Function} [params.callbacks.afterFetch] - Function to call after successfully fetching the omnibus data.
 * @param {Function} [params.callbacks.errorCallback] - Function to call if an error occurs during the fetch.
 *
 * @returns {Promise<Object|boolean>} A promise that resolves to the omnibus data object, or `false` if neither `brandKey` nor `sku` is provided.
 * @throws {Error} Will throw an error if the network request fails or if the response is not ok.
 */
const getOmnibus = async ({
  brandKey = window?.dAgency?.strings?.omnibusBrandKey,
  sku = '',
  currency = '',
  callbacks: { afterFetch, errorCallback } = {},
}) => {
  if (!brandKey && !sku) {
    console.error('Cannot fetch omnibus data, no brandKey or sku provided');
    return false;
  }

  const baseUrl = 'https://omnibus.filoblu.com/';
  const market = `${window.location.host}${window.Shopify.routes.root}`;
  const currencyValue = currency ? '&currency=' + currency : '';
  const omnibus = await fetch(
    `${baseUrl}?brand=${brandKey}&sku=${sku}&market=${market}${currencyValue}`,
  )
    .then((res) => {
      if (!res.ok) {
        const err = new Error(
          `Fetch result was not ok. Requested URL: ${res.url}`,
        );
        if (typeof errorCallback !== 'undefined') errorCallback(err);
        console.error('Cart update failed:', err);
        throw err;
      }
      return res.json();
    })
    .catch(async (err) => {
      if (typeof errorCallback !== 'undefined') await errorCallback(err);
      console.error('Cart update failed:', err);
      throw err;
    });
  if (typeof afterFetch !== 'undefined') await afterFetch(omnibus);
  return omnibus;
};

if (!window.dAgency) window.dAgency = {};
window.dAgency.initProductPage = (context) => {
  const sectionContainer = context.classList.contains('.shopify-section')
    ? context
    : context.closest('.shopify-section');
  if (!sectionContainer) return;

  const wishlistButton = sectionContainer.querySelector('[data-wishlist-btn]');
  const gallery = sectionContainer.querySelector(
    '.product-page__gallery-medias',
  );
  if (gallery) {
    const icons = window?.dAgency?.icons;
    const lightbox = new PhotoSwipeLightbox({
      gallery: gallery,
      children: '.product-page__gallery-slide--image',
      pswpModule: PhotoSwipe,
      mainClass: 'pswp--custom',
      arrowPrev: true,
      arrowNext: true,
      zoom: false,
      close: true,
      counter: false,
      bgOpacity: 1,
      showHideAnimationType: 'fade',
      ...(icons
        ? {
            ...(icons.chevronLeft && icons.chevronRight
              ? {
                  arrowPrevSVG: icons.chevronLeft,
                  arrowNextSVG: icons.chevronRight,
                }
              : {}),
            ...(icons.close ? { closeSVG: icons.close } : {}),
          }
        : {}),
    });
    lightbox.init();

    const leftArrow = sectionContainer.querySelector(
      '.product-page__gallery-arrow--left',
    );
    const rightArrow = sectionContainer.querySelector(
      '.product-page__gallery-arrow--right',
    );
    const pagination = sectionContainer.querySelector(
      '.product-page__gallery-pagination',
    );
    const options = {
      loop: true,
      modules: [
        ...(leftArrow && rightArrow ? [Navigation] : []),
        ...(pagination ? [Pagination] : []),
      ],
      ...(leftArrow && rightArrow
        ? {
            navigation: {
              prevEl: leftArrow,
              nextEl: rightArrow,
            },
          }
        : {}),
      ...(pagination
        ? {
            pagination: {
              el: pagination,
              clickable: true,
            },
          }
        : {}),
    };

    if (gallery.dataset.desktopMode != 'carousel') {
      var swiperGallery;
      const lgUp = window.matchMedia(`(min-width: ${breakpoints.lg})`);
      const handleSwiper = () => {
        if (lgUp.matches) {
          if (swiperGallery && swiperGallery.destroy)
            swiperGallery.destroy(true, true);
        } else {
          swiperGallery = new Swiper(gallery, options);
        }
      };
      handleSwiper();
      lgUp.addEventListener('change', handleSwiper);
    } else {
      const swiperGallery = new Swiper(gallery, options);
      const thumbnails = sectionContainer.querySelectorAll(
        '.product-page__gallery-thumbnails-item[data-index]',
      );
      if (thumbnails.length > 0) {
        thumbnails.forEach((thumbnail) =>
          thumbnail.addEventListener('click', () =>
            swiperGallery.slideToLoop(thumbnail.dataset.index),
          ),
        );
        swiperGallery.on('realIndexChange', () =>
          thumbnails.forEach(
            (thumbnail) =>
              (thumbnail.dataset.active =
                thumbnail.dataset.index == swiperGallery.realIndex),
          ),
        );
      }
    }
  }

  const scollSlides = context.querySelectorAll('.product-page__gallery-slide');
  const scrollBullets = context.querySelectorAll(
    '.product-page__gallery-scroll-pagination-bullet',
  );

  const updateActiveBullet = (activeCounter) => {
    scrollBullets.forEach((bullet) => {
      bullet.classList.toggle(
        'active',
        bullet.dataset.slideCounter === activeCounter,
      );
    });
  };

  const scrollObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const activeCounter = entry.target.dataset.slideCounter;
          updateActiveBullet(activeCounter);
        }
      });
    },
    {
      threshold: 0.5,
    },
  );

  scollSlides.forEach((slide) => scrollObserver.observe(slide));

  const form = sectionContainer.querySelector('.product-page__form');
  if (form) {
    const submit = form.querySelector('[type="submit"]');
    const errorWrapper = form.querySelector('.product-page__error-wrapper');
    const errorMessage = form.querySelector(
      '.product-page__error [data-message]',
    );
    const updateUrl = (variantId) => {
      const search = window.location.search
        .replace(/^\?/, '')
        .split('&')
        .reduce(
          (args, curr) => (/^variant=/.test(curr) ? args : [...args, curr]),
          [`variant=${variantId}`],
        )
        .filter((arg) => !!arg)
        .join('&');
      history.replaceState(null, '', `${window.location.pathname}?${search}`);
    };
    const updateOptions = ({ variant: selectedVariant, product }) => {
      const options = selectedVariant.options.reduce(
        (acc, option, index) => ({
          ...acc,
          [`option${index + 1}`]: option,
        }),
        {},
      );

      sectionContainer
        .querySelectorAll('.product-page__option-value input')
        .forEach((input) => {
          const currentOptions = {
            ...options,
            [input.name]: input.value,
          };
          const foundVariant = product.variants.find((variant) =>
            Object.keys(currentOptions).every(
              (optionName) => variant[optionName] == currentOptions[optionName],
            ),
          );
          input.disabled =
            !input.checked && foundVariant && !foundVariant?.available;
        });
    };

    const omnibusContainer = sectionContainer.querySelector(
      '.omnibus__container[data-sku][data-variant-price]',
    );
    const omnibusValue = sectionContainer.querySelector(
      '.omnibus__container [data-omnibus-value]',
    );
    const updateOmnibus = () => {
      if ((omnibusValue))
        getOmnibus({
          sku: omnibusContainer.dataset.sku,
          currency: omnibusContainer.dataset.currency,
          callbacks: {
            afterFetch: (omnibus) => {
              if (omnibus?.price) {
                if (parseInt(omnibusContainer.dataset.promo) == 1) {
                  omnibusValue.innerText = formatPrice({
                    value: omnibus.price * 100,
                  });
                  omnibusContainer.classList.remove('hidden');
                } else {
                  omnibusContainer.classList.add('hidden');
                }
              } else {
                omnibusContainer.classList.add('hidden');
              }
            },
            errorCallback: () => {
              omnibusContainer.classList.add('hidden');
            },
          },
        });
    };
    updateOmnibus();

    initProductForm({
      form,
      callbacks: {
        afterVariantChange: async ({ variant, product }) => {
          updateUrl(variant.id);
          const optionSelector = sectionContainer.querySelector(
            '.product-page__option details',
          );
          if (optionSelector) {
            optionSelector.removeAttribute('open');
          }
          variant.options.forEach((option, index) => {
            const label = sectionContainer.querySelector(
              `.option${index + 1}[data-current-value]`,
            );
            if (label) label.innerText = option;
          });
          errorWrapper.classList.remove('shown');

          updateOptions({ variant, product });
          if (omnibusContainer && omnibusValue && variant.sku) {
            omnibusContainer.dataset.sku = variant.sku;
            omnibusContainer.dataset.variantPrice = variant.price;
            updateOmnibus();
          }

          if (wishlistButton) {
            wishlistButton.dataset.wishlistVariantId = variant?.id;
            wishlistButton.dataset.wishlistPrice = formatPrice({
              value: variant?.price ?? 0,
            });
            wishlistButton.dataset.wishlistCompareAtPrice =
              variant?.compare_at_price
                ? formatPrice({
                    value: variant?.compare_at_price,
                  })
                : '';

            const wishlist = await getWishlist();
            const wishlistProduct = wishlist?.listcontents?.find(
              (addedProduct) =>
                addedProduct.epi == variant.id &&
                addedProduct.empi == product.id,
            );
            wishlistButton.dataset.wishlistStatus = wishlistProduct
              ? 'active'
              : 'inactive';
          }
        },
        beforeSubmit: () => {
          if (submit) submit.classList.add('loading');
        },
        afterSubmit: async () => {
          await openMinicart(true);
          if (submit) submit.classList.remove('loading');
        },
        errorCallback: (err) => {
          if (submit) submit.classList.remove('loading');
          if (errorWrapper && errorMessage && err?.data?.message) {
            errorMessage.innerText = err.data.message;
            requestAnimationFrame(() => {
              errorWrapper.classList.add('shown');
              setTimeout(() => {
                errorWrapper.classList.remove('shown');
              }, 3000);
            });
          }
        },
      },
    });
  }

  if (wishlistButton) {
    const toggleLoading = throttle(() => {
      wishlistButton.classList.toggle('loading');
    }, 500);
    wishlistButton.addEventListener('click', async (e) => {
      e.preventDefault();
      toggleLoading();
      const variantID = wishlistButton?.dataset?.wishlistVariantId;
      const masterID = wishlistButton?.dataset?.wishlistProductId;
      const productURL = wishlistButton?.dataset?.wishlistProductUrl;
      const price = wishlistButton?.dataset?.wishlistPrice;
      const compareAtPrice = wishlistButton?.dataset?.wishlistCompareAtPrice;

      if (wishlistButton?.dataset?.wishlistStatus === 'active') {
        await deleteFromWishlist({ variantID, masterID, productURL });
        wishlistButton.dataset.wishlistStatus = 'inactive';
      } else {
        await addToWishlist({
          variantID,
          masterID,
          productURL,
          compareAtPrice,
          price,
        });
        wishlistButton.dataset.wishlistStatus = 'active';
      }
      toggleLoading();
    });
  }
};

execOnLoad(window.dAgency.initProductPage, '.product-page__section');

if (!window.dAgency) window.dAgency = {};
window.dAgency.initProductRecommendations = async (context) => {
  const sectionContainer = context.classList.contains('.shopify-section')
    ? context
    : context.closest('.shopify-section');
  if (!sectionContainer) return;

  const wrapper = sectionContainer.querySelector(
    '.product-recommendations__container',
  );
  if (wrapper) {
    const productId = wrapper?.dataset?.productId;
    if (productId)
      await fetch(
        `${window.Shopify.routes.root}recommendations/products?product_id=${productId}&limit=12&intent=related&section_id=product-recommendations`,
      )
        .then((res) => res.text())
        .then((data) => {
          const sectionDom = new DOMParser().parseFromString(data, 'text/html');
          const newWrapper = sectionDom.querySelector(
            '.product-recommendations__container',
          );
          if (newWrapper) wrapper.innerHTML = newWrapper.innerHTML;

          const carouselContainer = wrapper.querySelector(
            '.carousel__container',
          );
          if (
            carouselContainer &&
            typeof window.dAgency.initCarousel == 'function'
          )
            window.dAgency.initCarousel(carouselContainer);

          if (typeof window.dAgency.initProductTile == 'function')
            wrapper
              .querySelectorAll('.product-tile__container')
              .forEach((tile) => window.dAgency.initProductTile(tile));
        });
  }
};

execOnLoad(
  window.dAgency.initProductRecommendations,
  '.product-recommendations__section',
);

if (!window.dAgency) window.dAgency = {};
window.dAgency.initSearchPage = (context) => {
  const sectionContainer = context.classList.contains('.shopify-section')
    ? context
    : context.closest('.shopify-section');
  if (!sectionContainer) return;

  initPagination({
    callbacks: {
      afterFetch: ({ components: { contentWrapperEl } }) => {
        if (typeof window?.dAgency?.initProductTile === 'function')
          contentWrapperEl
            .querySelectorAll('.product-tile__container')
            .forEach((tile) => window.dAgency.initProductTile(tile));
      },
    },
  });
};

execOnLoad(window.dAgency.initSearchPage, '.search-page__section');

if (!window.dAgency) window.dAgency = {};
window.dAgency.initSlideshow = (context) => {
  const sectionContainer = context.classList.contains('.shopify-section')
    ? context
    : context.closest('.shopify-section');
  if (!sectionContainer) return;

  const slideshow = sectionContainer.querySelector('.slideshow__container');
  if (!slideshow) return;

  const autoplaySpeed = parseFloat(slideshow?.dataset?.autoplaySpeed);
  const prevArrow = slideshow.querySelector('.slideshow__arrow--left');
  const nextArrow = slideshow.querySelector('.slideshow__arrow--right');
  const pagination = slideshow.querySelector('.slideshow__pagination');

  const options = {
    loop:
      slideshow.classList.contains('slideshow__container--loop') &&
      slideshow.querySelectorAll('.swiper-slide').length >= 2,
    modules: [
      ...(!isNaN(autoplaySpeed) && autoplaySpeed > 0 ? [Autoplay] : []),
      ...(prevArrow && nextArrow ? [Navigation] : []),
      ...(pagination ? [Pagination] : []),
    ],
    ...(!isNaN(autoplaySpeed) && autoplaySpeed > 0
      ? {
          autoplay: {
            enabled: true,
            delay: autoplaySpeed * 1000,
          },
        }
      : {}),
    ...(prevArrow && nextArrow
      ? {
          navigation: {
            prevEl: prevArrow,
            nextEl: nextArrow,
          },
        }
      : {}),
    ...(pagination
      ? {
          pagination: {
            el: pagination,
            clickable: true,
          },
        }
      : {}),
  };

  new Swiper(slideshow, options);
};

execOnLoad(window.dAgency.initSlideshow, '.slideshow__section');

if (!window.dAgency) window.dAgency = {};
window.dAgency.initSpotlight = (context) => {
  const sectionContainer = context.classList.contains('.shopify-section')
    ? context
    : context.closest('.shopify-section');
  if (!sectionContainer) return;

  const pins = sectionContainer.querySelectorAll('.spotlight__pin');
  pins.forEach((pin) => {
    const inner = pin.querySelector('.spotlight__pin-inner');
    if (!inner) return;
    inner.addEventListener('click', () => {
      pins.forEach((el) => {
        if (el.id != pin.id) el.classList.remove('open');
      });
      pin.classList.toggle('open');
    });
  });
};

execOnLoad(window.dAgency.initSpotlight, '.spotlight__section');

if (!window.dAgency) window.dAgency = {};
window.dAgency.initSubcategories = (context) => {
  const sectionContainer = context.classList.contains('.shopify-section')
    ? context
    : context.closest('.shopify-section');
  if (!sectionContainer) return;

  const slider = sectionContainer.querySelector('.subcategories__slider');
  if (!slider) return;

  new Swiper(slider, {
    loop: false,
    modules: [Navigation, FreeMode],
    navigation: {
      prevEl: sectionContainer.querySelector('.subcategories__arrow--left'),
      nextEl: sectionContainer.querySelector('.subcategories__arrow--right'),
    },
    freeMode: {
      enabled: true,
    },
    slidesPerView: 'auto',
  });
};

execOnLoad(window.dAgency.initSubcategories, '.subcategories__section');

if (!window.dAgency) window.dAgency = {};
window.dAgency.initWishlist = async (context) => {
  const sectionContainer = context.classList.contains('.shopify-section')
    ? context
    : context.closest('.shopify-section');
  if (!sectionContainer) return;

  const productsContainer = sectionContainer.querySelector(
    '.wishlist__products-list',
  );
  if (!productsContainer) return;

  const renderWishlistProducts = async () => {
    const wishlist = await getWishlist();

    if (wishlist.listcontents.length != 0) {
      const template = sectionContainer.querySelector(
        '[data-wishlist-template]',
      );
      if (!template) return;

      wishlist?.listcontents?.forEach(async (content) => {
        const clonedTemplate = template.cloneNode(true);

        clonedTemplate.classList.remove('hidden');

        const img = clonedTemplate.querySelector('a.product-tile__gallery img');
        if (img) {
          if (content?.iu) {
            img.src = content.iu;
          } else {
            // If the image is not available, try to get it from the product;
            // otherwise, leave the placeholder image
            const product = await getProduct({ url: content.du });
            if (product && product.featured_image) {
              img.src = product.featured_image;
            }
          }
          img.closest('.product-tile__gallery').href = content.du;
        }

        const wishListBtn = clonedTemplate.querySelector('[data-wishlist-btn]');
        if (wishListBtn) {
          if (content?.epi && wishListBtn?.dataset?.wishlistVariantId)
            wishListBtn.dataset.wishlistVariantId = content.epi;
          if (content?.empi && wishListBtn?.dataset?.wishlistProductId)
            wishListBtn.dataset.wishlistProductId = content.empi;
          if (content?.du && wishListBtn?.dataset?.wishlistProductUrl)
            wishListBtn.dataset.wishlistProductUrl = content.du;

          if (content?.epi && content?.empi && content?.du) {
            wishListBtn.addEventListener('click', async (e) => {
              e.preventDefault();
              clonedTemplate.classList.add('loading');
              wishListBtn.classList.add('loading');
              wishListBtn.dataset.wishlistStatus = 'inactive';
              await deleteFromWishlist({
                variantID: content.epi,
                masterID: content.empi,
                productURL: content.du,
              });
              clonedTemplate.remove();
            });
          }
        }

        const quickBuy = clonedTemplate.querySelector(
          '.product-tile__quick-buy',
        );
        if (quickBuy && content?.epi) {
          quickBuy.querySelector('button').disabled = false;
          quickBuy.addEventListener('click', async (e) => {
            e.preventDefault();
            addItemToCart({
              id: content.epi,
              quantity: 1,
              callbacks: {
                afterFetch: () => {
                  openMinicart(true);
                },
              },
            });
          });
        }

        const vendor = clonedTemplate.querySelector('.product-tile__vendor');
        if (vendor && content?.bt) vendor.textContent = content.bt;

        const title = clonedTemplate.querySelector('.product-tile__title');
        if (title) {
          if (content?.du) title.href = content.du;
          if (content?.dt) title.querySelector('p').textContent = content.dt;
        }

        const compare_at_price = clonedTemplate.querySelector(
          '[data-compare-at-price]',
        );
        if (compare_at_price && content?.cprops?.compare_at_price)
          compare_at_price.textContent = content.cprops.compare_at_price;

        const price = clonedTemplate.querySelector('[data-price]');
        if (price && content?.cprops?.price)
          price.textContent = content.cprops.price;

        productsContainer.classList.remove('hidden');

        productsContainer.appendChild(clonedTemplate);
      });
    } else {
      const emptyWishlist = sectionContainer.querySelector(
        '[data-wishlist-empty]',
      );

      if (emptyWishlist) emptyWishlist.classList.remove('hidden');
    }
  };

  if (!window.SwymCallbacks) window.SwymCallbacks = [];
  window.SwymCallbacks.push(renderWishlistProducts);
};

execOnLoad(window.dAgency.initWishlist, '.wishlist__section');

if (!window.dAgency) window.dAgency = {};
window.dAgency.initDesktopMenu = (context) => {
  const snippetContainer = context.classList.contains(
    '.desktop-menu__container',
  )
    ? context
    : context.closest('.desktop-menu__container');
  if (!snippetContainer) return;

  const firstLevels = [
    ...snippetContainer.querySelectorAll('.desktop-menu__first-level-link'),
  ];
  firstLevels.forEach((firstLevel) => {
    const wrapper = firstLevel.querySelector(':scope > span, :scope > a');
    const text = firstLevel.querySelector(
      ':scope > a > span, :scope > span > span',
    );
    const dropdown = firstLevel.querySelector('.desktop-menu__dropdown');
    if (!wrapper || !text) return;

    wrapper.addEventListener('mouseenter', () => {
      if (
        firstLevel.classList.contains('open') ||
        firstLevels.some((el) => el.classList.contains('open')) ||
        !dropdown
      )
        return;
      firstLevel.classList.add('open');
    });

    text.addEventListener('mouseenter', () => {
      if (firstLevel.classList.contains('open')) return;
      firstLevels.forEach((el) => el.classList.remove('open'));
      if (!dropdown) return;
      firstLevel.classList.add('open');
    });
  });

  snippetContainer.addEventListener('mouseleave', () => {
    firstLevels.forEach((dropdown) => dropdown.classList.remove('open'));
  });
};

execOnLoad(window.dAgency.initDesktopMenu, '.desktop-menu__container');

if (!window.dAgency) window.dAgency = {};
window.dAgency.initFilters = (context) => {
  const snippetContainer = context.classList.contains('.filters__container')
    ? context
    : context.closest('.filters__container');
  if (!snippetContainer) return;

  const form = snippetContainer.querySelector('.filters__form');
  if (!form) return;
  const modal = snippetContainer.closest('.modal');
  const filterContentSelector = '.filters__content';
  const itemsCounterSelector = '[data-items-counter]';
  const resultsWrapper = snippetContainer
    .closest('.shopify-section')
    .querySelector(filterContentSelector);
  const itemCounters = snippetContainer
    .closest('.shopify-section')
    .querySelectorAll(itemsCounterSelector);
  if (!resultsWrapper) {
    console.error(
      `Cannot find ${filterContentSelector} element. Cannot initialize filters`,
    );
    return;
  }
  const urlCache = {};

  const getPageContent = async (url) => {
    if (!url) return;
    if (!urlCache[url]) {
      const page = await fetch(url).then((res) => res.text());
      urlCache[url] = page;
    }
    return new DOMParser().parseFromString(urlCache[url], 'text/html');
  };

  const updatePage = debounce(async () => {
    if (modal) closeModal(modal);
    const searchParams = new URLSearchParams(new FormData(form)).toString();
    const newUrl = `${window.location.pathname}?${searchParams}`;
    const pageContent = await getPageContent(newUrl);
    const newForm = pageContent.querySelector(`#${form.id}`);
    const newContent = newForm
      .closest('.shopify-section')
      .querySelector(filterContentSelector);
    resultsWrapper.innerHTML = newContent.innerHTML;
    history.replaceState(null, '', newUrl);
    if (typeof window?.dAgency?.initProductTile === 'function')
      resultsWrapper
        .querySelectorAll('.product-tile__container')
        .forEach((tile) => window.dAgency.initProductTile(tile));
    initPagination({
      callbacks: {
        afterFetch: ({ components: { contentWrapperEl } }) => {
          if (typeof window?.dAgency?.initProductTile === 'function')
            contentWrapperEl
              .querySelectorAll('.product-tile__container')
              .forEach((tile) => window.dAgency.initProductTile(tile));
        },
      },
    });
    const newCount = newForm
      .closest('.shopify-section')
      .querySelector(itemsCounterSelector);
    if (newCount)
      itemCounters.forEach(
        (itemCounter) => (itemCounter.innerHTML = newCount.innerHTML),
      );
  }, 500);

  const debounceLoading = throttle(
    () => resultsWrapper.classList.toggle('loading'),
    1000,
  );

  const placeholder_sorting = document.querySelector(
    '.filters_placeholder form',
  );
  const target_sorting = document.querySelector('#collectionFilters');

  if (placeholder_sorting && target_sorting) {
    placeholder_sorting.addEventListener('change', async (e) => {
      let sorting_input = target_sorting.elements[e.target.name];
      if (sorting_input) {
        sorting_input.value = event.target.value;
        target_sorting.dispatchEvent(new Event('change'));
        placeholder_sorting.querySelector('label span').innerText =
          e.target.selectedOptions[0].text;
        target_sorting.querySelector('label.filters__sorting span').innerText =
          e.target.selectedOptions[0].text;
      }
    });
  }

  form.addEventListener('change', async (e) => {
    e.preventDefault();
    if (/-Search$/.test(e.target.id)) return;
    debounceLoading();
    await updatePage();
    debounceLoading();
  });

  const sortingWrapper = form.querySelector('.filters__sorting');
  if (sortingWrapper) {
    const select = sortingWrapper.querySelector('select');
    const text = sortingWrapper.querySelector('span');

    if (select && text)
      select.addEventListener(
        'change',
        (e) => (text.innerText = e.target.options[e.target.selectedIndex].text),
      );
  }

  const priceRange = form.querySelector('.filters__price');
  if (priceRange) {
    const rangeMin = priceRange.querySelector('input[type="range"][range-min]');
    const rangeMax = priceRange.querySelector('input[type="range"][range-max]');
    const minValueLabel = priceRange.querySelectorAll(
      '.filters__price-min, .filters__price-value-min',
    );
    const maxValueLabel = priceRange.querySelectorAll(
      '.filters__price-max, .filters__price-value-max',
    );

    rangeMin.addEventListener('input', () =>
      minValueLabel.forEach(
        (label) =>
          (label.innerText = formatPrice({
            format: window?.dAgency?.strings?.moneyFormat?.replace(
              /{{.*?}}/,
              '{{amount_no_decimals_with_comma_separator}}',
            ),
            value: parseInt(rangeMin.value) * 100,
          })),
      ),
    );

    rangeMax.addEventListener('input', () =>
      maxValueLabel.forEach(
        (label) =>
          (label.innerText = formatPrice({
            format: window?.dAgency?.strings?.moneyFormat?.replace(
              /{{.*?}}/,
              '{{amount_no_decimals_with_comma_separator}}',
            ),
            value: parseInt(rangeMax.value) * 100,
          })),
      ),
    );
  }

  form
    .querySelectorAll('.filters__accordion .filters__search input[type="text"]')
    .forEach((search) => {
      const labelTexts = search
        .closest('.filters__accordion')
        .querySelectorAll('label span:not(.filters__value-swatch)');

      search.addEventListener('input', () =>
        labelTexts.forEach((text) => {
          const label = text.closest('label');
          label.style.display =
            !search.value ||
            text.innerText.toLowerCase().indexOf(search.value.toLowerCase()) >=
              0
              ? ''
              : 'none';
        }),
      );
    });
};

execOnLoad(window.dAgency.initFilters, '.filters__container');

if (!window.dAgency) window.dAgency = {};
window.dAgency.initFooter = (context) => {
  const footerContainer = context.classList.contains('.mobile-menu__container')
    ? context
    : context.closest('.footer-c__container');
  if (!footerContainer) return;

  const newsletterTrigger = footerContainer.querySelector(
    '.footer-c__newsletter-input',
  );
  if (newsletterTrigger) {
    const newsletterFormId =
      newsletterTrigger.getAttribute('data-newsletter-id');
    if (newsletterFormId) {
      newsletterTrigger.addEventListener('click', () => {
        window._klOnsite = window._klOnsite || [];
        window._klOnsite.push(['openForm', newsletterFormId]);
      });
    }
  }
};

execOnLoad(window.dAgency.initFooter, '.footer-c__container');

if (!window.dAgency) window.dAgency = {};
window.dAgency.initMobileMenu = (context) => {
  const snippetContainer = context.classList.contains('.mobile-menu__container')
    ? context
    : context.closest('.mobile-menu__container');
  if (!snippetContainer) return;
  const header = document.querySelector('.header__background');
  const menuButton = snippetContainer.querySelector('.mobile-menu__icon');
  const modal = snippetContainer.querySelector('.modal');
  if (!menuButton || !modal) return;

  menuButton.addEventListener('click', () => {
    if (modal.classList.contains('open')) {
      snippetContainer.classList.remove('open');
      closeModal(modal);
      header.dataset.transparent = 'true';
      snippetContainer
        .querySelectorAll(
          '.mobile-menu__second-level.open, .mobile-menu__third-level.open',
        )
        .forEach((level) => level.classList.remove('open'));
    } else {
      const announcementHeight = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--announcement-bar-height',
        ),
      );
      if (window.scrollY < announcementHeight)
        window.scrollTo({ top: announcementHeight, behavior: 'smooth' });

      snippetContainer.classList.add('open');
      openModal(modal);
      header.dataset.transparent = 'false';
    }
  });

  const lgUp = window.matchMedia(`(min-width: ${breakpoints.lg})`);
  const handleNavigation = () => {
    if (lgUp.matches && snippetContainer.classList.contains('open')) {
      snippetContainer.classList.remove('open');
      closeModal(modal);
      header.dataset.transparent = 'true';
      snippetContainer
        .querySelectorAll(
          '.mobile-menu__second-level.open, .mobile-menu__third-level.open',
        )
        .forEach((level) => level.classList.remove('open'));
    }
  };
  lgUp.addEventListener('change', handleNavigation);
  snippetContainer
    .querySelectorAll('.mobile-menu__first-level--openable')
    .forEach((firstLevel) => {
      const secondLevel = firstLevel.querySelector(
        '.mobile-menu__second-level',
      );
      const opener = firstLevel.querySelector('.mobile-menu__first-level-link');
      if (!secondLevel || !opener) return;

      opener.addEventListener('click', () => {
        secondLevel.classList.add('open');
        secondLevel
          .querySelectorAll('.mobile-menu__third-level.open')
          .forEach((thirdLevel) => thirdLevel.classList.remove('open'));
      });
    });
  snippetContainer
    .querySelectorAll('.mobile-menu__second-level')
    .forEach((secondLevel) => {
      const back = secondLevel.querySelector('.mobile-menu__back');
      if (back) {
        back.addEventListener('click', () =>
          secondLevel.classList.remove('open'),
        );
      }

      secondLevel
        .querySelectorAll('.mobile-menu__second-level-item--openable')
        .forEach((secondLevelItem) => {
          const thirdLevel = secondLevelItem.querySelector(
            '.mobile-menu__third-level',
          );
          const opener = secondLevelItem.querySelector(
            '.mobile-menu__second-level-link',
          );
          if (!thirdLevel || !opener) return;

          opener.addEventListener('click', () =>
            thirdLevel.classList.add('open'),
          );
        });
    });

  snippetContainer
    .querySelectorAll('.mobile-menu__third-level')
    .forEach((thirdLevel) => {
      const back = thirdLevel.querySelector('.mobile-menu__back');
      if (!back) return;

      back.addEventListener('click', () => thirdLevel.classList.remove('open'));
    });
};

execOnLoad(window.dAgency.initMobileMenu, '.mobile-menu__container');

if (!window.dAgency) window.dAgency = {};
window.dAgency.initPrivacyCheckbox = (context) => {
  const snippetContainer = context.classList.contains(
    '.privacy-checkbox__container',
  )
    ? context
    : context.closest('.privacy-checkbox__container');
  if (
    !snippetContainer ||
    snippetContainer.classList.contains(
      'privacy-checkbox__container--initialized',
    )
  )
    return;

  const privacyCheckbox = snippetContainer.querySelector(
    'input[type="checkbox"]',
  );
  const formElement = snippetContainer.querySelector('.form-element');
  const form = snippetContainer.closest('form');
  if (privacyCheckbox && formElement && form) {
    const submit = form.querySelector('[type="submit"]');
    privacyCheckbox.addEventListener('invalid', () => {
      if (submit) submit.disabled = true;
      formElement.classList.add('error');
    });

    privacyCheckbox.addEventListener('change', () => {
      if (submit) submit.disabled = false;
      formElement.classList.remove('error');
    });
  }
  snippetContainer.classList.add('privacy-checkbox__container--initialized');
};

execOnLoad(window.dAgency.initPrivacyCheckbox, '.privacy-checkbox__container');

if (!window.dAgency) window.dAgency = {};
window.dAgency.initSearchBar = (context) => {
  const snippetContainer = context.classList.contains('.search-bar__container')
    ? context
    : context.closest('.search-bar__container');
  if (!snippetContainer) return;

  if (snippetContainer?.dataset?.predictiveEnabled == 'false') return;

  const form = snippetContainer.querySelector('.search-bar__form');
  if (!form) return;

  const queryField = form.querySelector('input[name="q"]');
  const categoriesWrapper = snippetContainer.querySelector(
    '.search-bar__results-categories',
  );
  const resultsWrapper = snippetContainer.querySelector('.search-bar__results');
  if (!queryField || !categoriesWrapper || !resultsWrapper) return;

  const cachedResults = [];

  const populateResults = (results, key) => {
    const typeWrapper = snippetContainer.querySelector(`[data-type="${key}"]`);
    if (!typeWrapper) return;

    const template = typeWrapper.querySelector('[data-template]');
    const resultsList = typeWrapper.querySelector('.search-bar__results-list');
    if (!template || !resultsList) return;

    resultsList
      .querySelectorAll('.search-bar__results-element:not([data-template])')
      .forEach((el) => el.remove());
    results.map((result) => {
      const element = template.cloneNode(true);
      element.removeAttribute('data-template');

      const title = element.querySelector('.search-bar__results-element-title');
      if (title) {
        if (key == 'query') {
          if (result.styled_text) title.innerHTML = result.styled_text;
        } else {
          if (result.title) title.innerText = result.title;
        }
      }

      const link = element.querySelector('a');
      if (link && result.url) {
        link.href = result.url;
        if (key == 'query') {
          link.addEventListener('click', (e) => {
            e.preventDefault();
            queryField.value = result.text;
            fetchSuggestions();
          });
        }
      }

      const imageWrapper = element.querySelector(
        '.search-bar__results-element-image',
      );
      const imageObj = result.image ?? result.featured_image;
      if (imageObj) {
        const src = typeof imageObj === 'string' ? imageObj : imageObj.src;
        if (imageWrapper) {
          if (src) imageWrapper.querySelector('img').src = src;
          else imageWrapper.remove();
        }
      } else {
        if (imageWrapper) imageWrapper.remove();
      }

      resultsList.appendChild(element);
    });
    typeWrapper.style.display = results.length > 0 ? 'block' : 'none';
  };

  const handleData = (data) => {
    if (!data?.resources?.results) return;
    const {
      articles = [],
      collections = [],
      pages = [],
      products = [],
      queries = [],
    } = data.resources.results;

    if (
      articles.length <= 0 &&
      collections.length <= 0 &&
      pages.length <= 0 &&
      queries.length <= 0
    ) {
      if (products.length > 0) {
        resultsWrapper.style.display = '';
        categoriesWrapper.style.display = 'none';
      } else {
        resultsWrapper.style.display = 'none';
      }
    } else {
      resultsWrapper.style.display = '';
      categoriesWrapper.style.display = '';
    }
    populateResults(articles, 'article');
    populateResults(collections, 'collection');
    populateResults(pages, 'page');
    populateResults(queries, 'query');
    populateResults(products, 'product');
  };

  const fetchSuggestions = debounce(() => {
    const params = new URLSearchParams(new FormData(form));
    if (!params.get('q')) {
      resultsWrapper.style.display = 'none';
      return;
    }
    form.classList.add('loading');

    const cachedResult = cachedResults.find(
      (cachedResult) => cachedResult.query === params.get('q'),
    );
    if (cachedResult) {
      handleData(cachedResult.data);
    } else {
      fetch(
        `${window.Shopify.routes.root}search/suggest.json?${params.toString()}`,
      )
        .then((res) => res.json())
        .then((data) => {
          handleData(data);
          cachedResults.push({
            query: params.get('q'),
            data,
          });
        })
        .catch((err) => {
          console.error(err);
        });
    }

    form.classList.remove('loading');
  }, 500);

  form.addEventListener('input', () => {
    fetchSuggestions();
  });
};

execOnLoad(window.dAgency.initSearchBar, '.search-bar__container');
